
import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { CampingSpot, AppUser, GpxRoute, RouteRegion, WikiSpot, ElevationPoint, MapBounds, CollaborativeMap, SpotVisit, ConditionTag, DayForecast, DiscoverPOI, Coordinates, SvvCamera, DailyWeather, SunFilterConfig, SunPeriod } from '../types';
import type { SidebarMode } from '../types/sidebar';
import { Star, MapPin, Navigation, Heart, Trash2, Edit3, Share2, MessageSquare, Plus, ChevronDown, ChevronUp, Search, X, Loader2, Filter, Mountain, Tent, Coffee, Info, RefreshCcw, ArrowLeft, Clock, Activity, ExternalLink, Image as ImageIcon, Map as MapIcon, TrendingUp, User, Route, Lock, Newspaper, CheckCircle2, ShieldCheck, Snowflake, Unlock, Waves, Bath, Signal, Eye, TrendingUp as TrendingUpIcon, Bell, BellOff, AlertTriangle, Sparkles, Sun, Sliders } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SpotCard from './spots/SpotCard';
import FeedCarousel from './spots/FeedCarousel';
import RouteDetailView from './routes/RouteDetailView';
import UserAvatar from './common/UserAvatar';
import { fetchRecentNewsSpots, fetchRecentReviews, RecentReview, getThumbUrl, isSpotInEnabledCountry } from '../services/firebaseService';
import { mergeNewsFeed, filterNewsFeed, spotPlaceLabel, hasRealCreatorName, creatorInitials, type NewsFilter } from '../services/newsFeed';
import { ROUTE_COLOR_PALETTE } from '../constants';
import DiscoverPanel from './discover/DiscoverPanel';
import { useToast } from '../contexts/ToastContext';
import { isWithinTrialPeriod, isAdminUser } from '../services/accessControl';
import AuthNudge from './common/AuthNudge';
import ReplayPromoCard from './replay/ReplayPromoCard';
import { detectCountry } from '../utils/countryDetection';
import { useLocationGate } from '../contexts/LocationGateContext';
import { coordKey, fetchForecastsForCoordinates, isSunnyForecast } from '../services/weatherService';
import SunFilterModal from './weather/SunFilterModal';
import SpotFilterModal from './spots/SpotFilterModal';

const DRILLDOWN_CAT: Record<string, { emoji: string; label: string }> = {
  motorhome: { emoji: '🚐', label: 'Bobil' }, campervan: { emoji: '🚌', label: 'Campervan' },
  caravan: { emoji: '🏠', label: 'Campingvogn' }, camper_trailer: { emoji: '🪝', label: 'Campinghenger' },
  rooftent: { emoji: '⛺', label: 'Taktelt' }, offroad: { emoji: '🚙', label: 'Offroad/4x4' },
};


/* ── Memoized news feed items ──────────────────────────────── */

function getSatelliteTileUrls(lat: number, lon: number, zoom = 16): string[] {
  const n = Math.pow(2, zoom);
  const x = Math.floor(((lon + 180) / 360) * n);
  const latRad = (lat * Math.PI) / 180;
  const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return [
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
    `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x + 1}`,
  ];
}

interface NewsSpotItemProps {
  spot: CampingSpot;
  isSelected: boolean;
  address?: string;
  onSelect: (spot: CampingSpot) => void;
  onSelectById: (id: string) => void;
  hasSelectHandler: boolean;
  t: any;
}

const NewsSpotItem = memo(({ spot, isSelected, address, onSelect, onSelectById, hasSelectHandler, t }: NewsSpotItemProps) => {
  // Bilde-presedens: brukerens egne bilder (photoUrls) vinner alltid, deretter
  // auto-cover (Google-foto / Street View / Mapillary via auto_image_url), og
  // først til slutt satellitt-tile. Speiler resten av appen (Map/MiniCards).
  const [coverFailed, setCoverFailed] = useState(false);
  const userPhotos = spot.photoUrls ?? [];
  const originalUrl = userPhotos[0] || spot.auto_image_url || '';
  const thumbUrl = originalUrl ? getThumbUrl(originalUrl) : '';
  // Kjente ekstra brukerbilder utover cover — gir karusellen riktig bilde-antall
  // uten henting, så piler kun vises når det faktisk finnes flere bilder.
  const extraUrls = userPhotos.length > 1 ? userPhotos.slice(1) : undefined;
  // Faller tilbake på satellitt hvis bilde-URL-en er død (unngår brutt bilde-ikon).
  const showImage = !!thumbUrl && !coverFailed;

  // "Nylig bekreftet"-kort viser bekrefteren (den som sist sjekket inn / anmeldte /
  // beriket plassen); "Ny"-kort viser skaperen. Faller tilbake til skaperen når
  // bekrefter-identitet mangler (eldre plasser bekreftet før denormaliseringen).
  const showConfirmer = spot.newsKind === 'confirmed' && !!(spot.confirmedByPhoto || spot.confirmedByName);
  const avatarPhoto = showConfirmer ? spot.confirmedByPhoto : spot.createdByPhoto;
  const avatarName = showConfirmer ? spot.confirmedByName : spot.createdByName;
  return (
    <div
      role="button"
      onClick={() => hasSelectHandler ? onSelect(spot) : onSelectById(spot.id)}
      className={`flex flex-col rounded-2xl overflow-hidden cursor-pointer border transition duration-300 ${isSelected ? 'bg-amber-500/10 border-amber-500/40 shadow-lg shadow-amber-500/5' : 'bg-[var(--bg-surface)] border-[var(--border-primary)] hover:border-[var(--border-secondary)] hover:shadow-md'}`}
    >
      {/* Banner Image / Satellite */}
      <div className="w-full h-32 sm:h-36 relative overflow-hidden bg-stone-900 group">
        {showImage ? (
          <FeedCarousel spotId={spot.id} coverUrl={originalUrl} extraUrls={extraUrls} onAllBroken={() => setCoverFailed(true)} />
        ) : spot.location ? (
          <div className="relative w-full h-full bg-stone-800 overflow-hidden">
            <div className="flex w-[200%] h-full" style={{ marginLeft: '-25%' }}>
              {getSatelliteTileUrls(spot.location.latitude, spot.location.longitude, 16).map((url, i) => (
                <img key={i} src={url} alt="" className="h-full flex-1 object-cover" loading="lazy" decoding="async" />
              ))}
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-brand/30 animate-ping pointer-events-none" style={{ margin: '-8px' }} />
              <div className="w-6 h-6 rounded-full bg-stone-950/80 backdrop-blur-sm border border-white/80 shadow flex items-center justify-center text-white">
                <MapPin size={12} className="text-emerald-400" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[var(--bg-input)]"><CheckCircle2 size={24} className="text-amber-400" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 pointer-events-none" />
        <div className="absolute top-2 left-2 z-20 px-2 py-0.5 rounded-lg bg-black/60 border border-white/10 backdrop-blur-sm flex items-center gap-1">
          <span className="text-3xs font-bold text-emerald-400 uppercase tracking-wider">
            {showImage ? t('spot.photo', 'Foto') : t('spot.satellite', 'Satellitt')}
          </span>
        </div>
        <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-lg bg-black/60 border border-white/10 backdrop-blur-sm flex items-center gap-1">
          <span className={`text-3xs font-bold uppercase tracking-wider ${spot.newsKind === 'confirmed' ? 'text-sky-300' : 'text-amber-400'}`}>
            {spot.newsKind === 'confirmed' ? t('sidebar.recently_confirmed', 'Nylig bekreftet') : t('sidebar.new_spot', 'Ny')}
          </span>
        </div>
      </div>

      {/* Content Area with Text & Avatar */}
      <div className="p-3.5 flex items-start justify-between gap-3 bg-[var(--bg-surface)]">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="text-sm font-black text-[var(--text-primary)] truncate">{spot.name || t('sidebar.unknown_place')}</p>
          <p className="flex items-center gap-1 text-tiny text-[var(--text-secondary)] truncate">
            <MapPin size={11} className="shrink-0 text-[var(--text-muted)]" />
            <span className="truncate">{spotPlaceLabel(spot) || address || `${spot.location.latitude.toFixed(3)}, ${spot.location.longitude.toFixed(3)}`}</span>
          </p>
        </div>
        {avatarPhoto ? (
          <div className="shrink-0 pt-0.5" title={avatarName || undefined}>
            <UserAvatar photoURL={avatarPhoto} tier={spot.createdByTier || 0} size="sm" />
          </div>
        ) : hasRealCreatorName(avatarName) ? (
          <div
            className="shrink-0 w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-xs"
            title={avatarName || undefined}
          >
            {creatorInitials(avatarName)}
          </div>
        ) : (
          <div
            className="shrink-0 w-8 h-8 rounded-xl bg-[var(--bg-input)] border border-[var(--border-primary)] flex items-center justify-center text-[var(--text-muted)]"
            title={t('spot.anonymous_contributor', 'Anonymt bidrag')}
          >
            <User size={16} />
          </div>
        )}
      </div>
    </div>
  );
});

interface NewsReviewItemProps {
  review: RecentReview;
  spot?: CampingSpot;
  onSelectSpot: (id: string) => void;
  t: (key: string, opts?: any) => string;
}

const NewsReviewItem = memo(({ review, spot, onSelectSpot, t }: NewsReviewItemProps) => {
  // Brukerbilder først (anmelderens eget foto → plassens brukerbilder), så plass-
  // snapshot fra anmeldelsen, så auto-cover (Google/Street View), og satellitt sist.
  const [coverFailed, setCoverFailed] = useState(false);
  const spotImg = spot?.photoUrls?.[0] || review.spotPhotoUrl || review.spotPhotoUrls?.[0] || spot?.auto_image_url || '';
  const originalUrl = review.photoUrl || spotImg || '';
  const thumbUrl = originalUrl ? getThumbUrl(originalUrl) : '';
  // Faller tilbake på satellitt hvis bilde-URL-en er død (unngår brutt bilde-ikon).
  const showImage = !!thumbUrl && !coverFailed;
  const location = spot?.location || review.spotLocation;

  return (
    <div
      role="button"
      onClick={() => review.spotId && onSelectSpot(review.spotId)}
      className="flex flex-col rounded-2xl overflow-hidden cursor-pointer border border-[var(--border-primary)] hover:border-[var(--border-secondary)] bg-[var(--bg-surface)] transition duration-300 hover:shadow-md group"
    >
      {/* Banner Image / Satellite */}
      <div className="w-full h-32 sm:h-36 relative overflow-hidden bg-stone-900 group">
        {showImage ? (
          <FeedCarousel spotId={review.spotId} coverUrl={originalUrl} extraUrls={review.photoUrls} onAllBroken={() => setCoverFailed(true)} />
        ) : location ? (
          <div className="relative w-full h-full bg-stone-800 overflow-hidden">
            <div className="flex w-[200%] h-full" style={{ marginLeft: '-25%' }}>
              {getSatelliteTileUrls(location.latitude, location.longitude, 16).map((url, i) => (
                <img key={i} src={url} alt="" className="h-full flex-1 object-cover" loading="lazy" decoding="async" />
              ))}
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full bg-brand/30 animate-ping pointer-events-none" style={{ margin: '-8px' }} />
              <div className="w-6 h-6 rounded-full bg-stone-950/80 backdrop-blur-sm border border-white/80 shadow flex items-center justify-center text-white">
                <MapPin size={12} className="text-emerald-400" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[var(--bg-input)]"><MessageSquare size={24} className="text-amber-400" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 pointer-events-none" />
        <div className="absolute top-2 left-2 z-20 px-2 py-0.5 rounded-lg bg-black/60 border border-white/10 backdrop-blur-sm flex items-center gap-1">
          <span className="text-3xs font-bold text-amber-400 uppercase tracking-wider">
            {t('sidebar.review', 'Vurdering')}
          </span>
        </div>
      </div>

      {/* Content Area with Text & Avatar */}
      <div className="p-3.5 flex items-start justify-between gap-3 bg-[var(--bg-surface)]">
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-black text-[var(--text-primary)] truncate">{review.spotName || spot?.name || t('route.anonymous')}</span>
            <div className="flex items-center gap-0.5 shrink-0 ml-2">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} size={10} className={i <= review.rating ? 'text-amber-400 fill-amber-400' : 'text-[var(--text-muted)]'} />
              ))}
            </div>
          </div>
          {spotPlaceLabel(spot) && (
            <p className="flex items-center gap-1 text-tiny text-[var(--text-secondary)] truncate">
              <MapPin size={11} className="shrink-0 text-[var(--text-muted)]" />
              <span className="truncate">{spotPlaceLabel(spot)}</span>
            </p>
          )}
          <p className="text-tiny text-[var(--text-secondary)] line-clamp-2 leading-relaxed">{review.comment || review.userName}</p>
        </div>
        <div className="shrink-0 pt-0.5">
          <UserAvatar photoURL={review.userPhoto} tier={review.userTier} size="sm" />
        </div>
      </div>
    </div>
  );
});

type RouteFacilityLoadingState = {
  routeId: string | null;
  fricamp: boolean;
  restAreas: boolean;
  campsites: boolean;
  attractions: boolean;
  chargers: boolean;
  wildlife: boolean;
};

interface SpotListProps {
  spots: CampingSpot[];
  allSpots?: CampingSpot[];
  selectedSpotId: string | null;
  activeHikingTrailId: string | null;
  onSelectSpot: (id: string) => void;
  onSelectNewsSpot?: (spot: CampingSpot) => void;
  onSelectHikingTrail: (id: string | null) => void;
  onRemoveSpot: (id: string) => void;
  onEditSpot: (spot: CampingSpot) => void;
  onResetOverview: () => void;
  onShowMap: () => void;
  isLoading: boolean;
  user: AppUser | null;
  onOpenAuth: () => void;
  showFavoritesOnly: boolean;
  onToggleFavoritesOnly: () => void;
  onOpenLegal: () => void;
  onOpenSymbolLegend: () => void;
  isAnalyzing: boolean;
  activeAnalysisIds: Set<string>;
  onReanalyze: (spot: CampingSpot) => void;
  isSearchActive: boolean;
  onClearSearch: () => void;
  onSaveSearchResult: (spot: CampingSpot) => void;
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  routes: GpxRoute[];
  activeRouteIds: Set<string>;
  setActiveRouteIds: (ids: Set<string>) => void;
  previewRegion: RouteRegion | null;
  setPreviewRegion: (region: RouteRegion | null) => void;
  onPreviewFilteredRouteIds?: (ids: Set<string> | null) => void;
  loadingRouteId: string | null;
  onToggleRoute: (route: GpxRoute) => void;
  isRegionLoading: boolean;
  onRefreshRoutes: () => void;
  onEditRoute: (route: GpxRoute) => void;
  routeCategoryFilters: Set<string>;
  toggleRouteCategoryFilter: (filter: string) => void;
  routeVehicleFilterOff?: boolean;
  onToggleRouteVehicleFilter?: () => void;
  mapZoom: number;
  layers: any;
  wikiSpots: WikiSpot[];
  isWikiLoading: boolean;
  onFitBounds: (bounds: [[number, number], [number, number]]) => void;
  currentViewBounds: MapBounds | null;
  onCreateRouteFromSpot?: (spot: CampingSpot) => void;
  creatingRouteSpotId?: string | null;

  selectedRouteId?: string | null;
  onCloseRouteDetails?: () => void;
  onShowOnMap?: () => void;
  onOpenRouteDetails?: (routeId: string) => void;

  onReanalyzeRoute?: (route: GpxRoute) => void;
  onReanalyzeRouteLayer?: (route: GpxRoute, layer: string) => void;
  analyzingRouteIds?: Set<string>;

  favoriteSpotIds?: string[];
  visitedSpotIds?: string[];
  collaborativeMaps?: CollaborativeMap[];
  onToggleFavoriteSpot?: (e: React.MouseEvent, spotId: string) => void;
  onToggleVisit?: (e: React.MouseEvent, spotId: string) => void;
  onAddPastStay?: (spotId: string, checkInAt: number, checkedOutAt?: number) => Promise<unknown> | void;
  onDeletePastStay?: (spotId: string, checkInAt: number) => Promise<unknown> | void;
  onMarkOccupied?: (spotId: string, days: number, tags: ConditionTag[]) => void;
  onExtendOccupancy?: (spotId: string, days: number) => void;
  onClearOccupancy?: (spotId: string) => void;
  userPosition?: Coordinates | null;
  onAddToMap?: (e: React.MouseEvent, spotId: string) => void;

  favoriteRouteIds?: string[];
  onToggleFavoriteRoute?: (routeId: string) => void;
  isPaidUser?: boolean;
  onOpenUpgrade?: () => void;
  onAddRouteToMap?: (routeId: string) => void;
  generatingMetaIds?: Set<string>;
  visits?: SpotVisit[];
  /** hasReplayAccess(user) && ≥2 innsjekk m/posisjon — styrer Reisefilm-promo-kortet. */
  replayPromoEligible?: boolean;
  onOpenReplay?: () => void;
  onOpenStickerBoard?: () => void;
  activeFilters?: Set<string>;
  onToggleFilter?: (id: string) => void;
  onClearFilters?: () => void;
  onUploadRoute?: () => void;
  onRouteStatusChange?: (routeId: string, newStatus: string) => void;
  routeFacilityLoading?: RouteFacilityLoadingState;
  canReturnToRoute?: boolean;
  onReturnToRoute?: () => void;
  canReturnToCollabMap?: boolean;
  onReturnToCollabMap?: () => void;
  overlaySpot?: CampingSpot | null;
  onCloseOverlaySpot?: () => void;
  onStartFollowRoute?: (route: GpxRoute) => void;
  onNavigateToRoute?: (route: GpxRoute) => void;
  trackingStatus?: 'idle' | 'recording' | 'paused';
  onShowNearestBarrier?: (spot: CampingSpot) => void;
  onEnsureSpotPersisted?: (spotId: string) => void;
  onNavigateInApp?: (spot: CampingSpot) => void;
  followMode?: 'route_follow' | 'free_drive_follow' | null;
  showFollowSummary?: boolean;
  discoverWeather?: DayForecast[];
  discoverAttractions?: DiscoverPOI[];
  discoverHeritage?: DiscoverPOI[];
  discoverLoading?: boolean;
  discoverError?: boolean;
  mapCenter?: Coordinates;
  discoverSelectedId?: string | null;
  onDiscoverSelect?: (id: string, lat: number, lon: number) => void;
  onDiscoverFilterChange?: (hiddenCategories: Set<string>) => void;
  followSummary?: {
    activeTargetName: string | null;
    activeTargetDistanceM: number | null;
    routeDistanceM: number | null;
    routeName?: string | null;
  } | null;
  isPremiumUser?: boolean;
  hasPremiumAccess?: (u: AppUser | null) => boolean;
  draftRouteDetail?: GpxRoute | null;
  draftRoutePath?: Coordinates[] | null;
  onCloseDraftRouteDetail?: () => void;
  onRouteWebcamsChange?: (cameras: SvvCamera[]) => void;
  enabledCountries?: Partial<Record<string, boolean>>;
}

const SpotList: React.FC<SpotListProps> = ({
  spots, allSpots = [], selectedSpotId, activeHikingTrailId, onSelectSpot, onSelectNewsSpot, onSelectHikingTrail, onRemoveSpot, onEditSpot, onResetOverview, onShowMap, isLoading, user, onOpenAuth,
  showFavoritesOnly, onToggleFavoritesOnly, onOpenLegal, onOpenSymbolLegend, isAnalyzing, activeAnalysisIds = new Set(), onReanalyze,
  isSearchActive, onClearSearch, onSaveSearchResult,
  sidebarMode, setSidebarMode, routes = [], activeRouteIds = new Set(), setActiveRouteIds, previewRegion, setPreviewRegion, onPreviewFilteredRouteIds, loadingRouteId, onToggleRoute, isRegionLoading, onRefreshRoutes, onEditRoute,
  routeCategoryFilters, toggleRouteCategoryFilter, routeVehicleFilterOff = false, onToggleRouteVehicleFilter, mapZoom = 14, layers, wikiSpots = [], isWikiLoading = false, onFitBounds, currentViewBounds,
  onCreateRouteFromSpot,
  creatingRouteSpotId = null,
  selectedRouteId, onCloseRouteDetails, onShowOnMap, onOpenRouteDetails,
  onReanalyzeRoute, onReanalyzeRouteLayer, analyzingRouteIds = new Set(),
  favoriteSpotIds = [], visitedSpotIds = [], collaborativeMaps = [], onToggleFavoriteSpot, onToggleVisit, onAddPastStay, onDeletePastStay, onMarkOccupied, onExtendOccupancy, onClearOccupancy, userPosition, onAddToMap,
  favoriteRouteIds = [], onToggleFavoriteRoute,
  isPaidUser = false, onOpenUpgrade, onAddRouteToMap,
  generatingMetaIds = new Set(),
  visits = [],
  replayPromoEligible = false,
  onOpenReplay,
  onOpenStickerBoard,
  activeFilters: activeFiltersProp = new Set(),
  onToggleFilter,
  onClearFilters,
  onUploadRoute,
  onRouteStatusChange,
  routeFacilityLoading,
  canReturnToRoute = false,
  onReturnToRoute,
  canReturnToCollabMap = false,
  onReturnToCollabMap,
  overlaySpot,
  onCloseOverlaySpot,
  onStartFollowRoute,
  onNavigateToRoute,
  trackingStatus = 'idle',
  onShowNearestBarrier,
  onEnsureSpotPersisted,
  onNavigateInApp,
  followMode = null,
  showFollowSummary = false,
  discoverWeather = [],
  discoverAttractions = [],
  discoverHeritage = [],
  discoverLoading = false,
  discoverError = false,
  mapCenter,
  discoverSelectedId = null,
  onDiscoverSelect,
  onDiscoverFilterChange,
  followSummary = null,
  isPremiumUser,
  hasPremiumAccess,
  draftRouteDetail = null,
  draftRoutePath = null,
  onCloseDraftRouteDetail,
  onRouteWebcamsChange,
  enabledCountries,
}) => {
  const activeFilters = activeFiltersProp;
  const { t } = useTranslation();
  const toast = useToast();
  const { gatedGetCurrentPosition } = useLocationGate();
  const [routeFilter, setRouteFilter] = useState<'all' | 'mine'>('all');
  const [expandedRegions, setExpandedRegions] = useState<Set<RouteRegion>>(new Set());
  const [recentNewsSpots, setRecentNewsSpots] = useState<CampingSpot[]>([]);
  const [recentReviews, setRecentReviews] = useState<RecentReview[]>([]);
  const [loadingNews, setLoadingNews] = useState(false);
  const [newsAddresses, setNewsAddresses] = useState<Record<string, string>>(() => {
    try {
      const cached = localStorage.getItem('fricamp_news_addresses');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [newsFilter, setNewsFilter] = useState<NewsFilter>('all');
  // Sammenslått, kronologisk NYHETER-feed (nye/bekreftede plasser + anmeldelser),
  // filtrert på valgt chip. Erstatter de tidligere «Nye steder»/«Anmeldelser»-fanene.
  const newsFeedItems = React.useMemo(
    () => filterNewsFeed(mergeNewsFeed(recentNewsSpots, recentReviews), newsFilter),
    [recentNewsSpots, recentReviews, newsFilter],
  );
  const [winterBannerDismissed, setWinterBannerDismissed] = useState(false);
  const [regionCategoryFilter, setRegionCategoryFilter] = useState<string | null>(null);
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set(['Norge']));
  const [seasonFilter, setSeasonFilter] = useState<'all' | 'year_round' | 'summer_only'>('all');
  const [showRouteFilterDropdown, setShowRouteFilterDropdown] = useState(false);

  // Sort by distance / popularity
  const [sortByDistance, setSortByDistance] = useState(false);
  const [sortByPopularity, setSortByPopularity] = useState(false);
  const [userPos, setUserPos] = useState<{ lat: number; lon: number } | null>(null);
  const [gettingPos, setGettingPos] = useState(false);

  // Bruk userPosition eller mapCenter som fallback (stabilisert for å unngå unødig re-fetch)
  const effectiveUserPos = React.useMemo(() => {
    if (userPos) return userPos;
    if (userPosition) return { lat: userPosition.latitude, lon: userPosition.longitude };
    if (mapCenter) return { lat: mapCenter.latitude, lon: mapCenter.longitude };
    return null;
  }, [userPos?.lat, userPos?.lon, userPosition?.latitude, userPosition?.longitude, mapCenter?.latitude, mapCenter?.longitude]);

  // ─── Soljakt: State & Værprognoser ─────────────────────────────────────
  const [sunFilterConfig, setSunFilterConfig] = useState<SunFilterConfig>(() => {
    try {
      const saved = localStorage.getItem('fricamp_sun_filter_config');
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      enabled: false,
      period: '3days',
      maxDistanceKm: 200,
      strictSun: false,
    };
  });
  const [showSunSettingsModal, setShowSunSettingsModal] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [forecastMap, setForecastMap] = useState<Map<string, DailyWeather[]>>(new Map());
  const [loadingForecasts, setLoadingForecasts] = useState(false);
  const isFetchingForecastsRef = useRef(false);

  const canUsePremiumNews = !!user && (
    isPaidUser ||
    isAdminUser(user) ||
    user.isTester === true
  );

  // Proximity alert — nearest unvisited favorite within 2 km
  const [nearbyFavorite, setNearbyFavorite] = useState<CampingSpot | null>(null);
  const [proximityDismissed, setProximityDismissed] = useState<string | null>(null);

  const isWinterSeason = (() => { const m = new Date().getMonth(); return m >= 10 || m <= 3; })();

  // Haversine distance in km
  const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const handleToggleSortByDistance = () => {
    if (sortByDistance) {
      setSortByDistance(false);
      return;
    }
    if (effectiveUserPos) {
      if (!userPos) setUserPos(effectiveUserPos);
      setSortByDistance(true);
      return;
    }
    setGettingPos(true);
    gatedGetCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setSortByDistance(true);
        setGettingPos(false);
      },
      () => {
        setGettingPos(false);
        if (mapCenter) {
          setUserPos({ lat: mapCenter.latitude, lon: mapCenter.longitude });
          setSortByDistance(true);
        } else {
          toast(t('sidebar.location_error'), 'error');
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleToggleSunFilter = () => {
    if (sunFilterConfig.enabled) {
      setSunFilterConfig(prev => {
        const next = { ...prev, enabled: false };
        try { localStorage.setItem('fricamp_sun_filter_config', JSON.stringify(next)); } catch {}
        return next;
      });
      return;
    }

    const activate = () => {
      setSortByDistance(true);
      setSunFilterConfig(prev => {
        const next = { ...prev, enabled: true };
        try { localStorage.setItem('fricamp_sun_filter_config', JSON.stringify(next)); } catch {}
        return next;
      });
    };

    if (effectiveUserPos) {
      if (!userPos) setUserPos(effectiveUserPos);
      activate();
      return;
    }

    setGettingPos(true);
    gatedGetCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setGettingPos(false);
        activate();
      },
      () => {
        setGettingPos(false);
        if (mapCenter) {
          setUserPos({ lat: mapCenter.latitude, lon: mapCenter.longitude });
          activate();
        } else {
          toast(t('sidebar.location_error'), 'error');
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  // Hent værvarsel for de nærmeste plassene når solfilteret er aktivt
  useEffect(() => {
    if (!sunFilterConfig.enabled || !effectiveUserPos) {
      setLoadingForecasts(false);
      return;
    }

    if (isFetchingForecastsRef.current) return;

    const pool = (allSpots && allSpots.length > 0 ? allSpots : spots)
      .filter(s => s && s.location && !s.isInvalid);

    // Beregn avstand til alle og sorter
    const withDist = pool.map(s => ({
      ...s,
      distance: haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude),
    }));

    // Filtrer på maks distanse hvis satt
    const radiusFiltered = sunFilterConfig.maxDistanceKm > 0
      ? withDist.filter(s => (s.distance ?? 9999) <= sunFilterConfig.maxDistanceKm)
      : withDist;

    radiusFiltered.sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));

    // Del opp i avstandsbånd slik at vi sampler kandidater spredt utover hele søkeradiusen
    // (ikke bare stopper ved de 25 nærmeste i samme nærområde):
    const bands: { min: number; max: number; target: number }[] = [];
    if (sunFilterConfig.maxDistanceKm === 0) {
      // Ubegrenset: sample representanter på tvers av hele Norge
      bands.push(
        { min: 0, max: 60, target: 6 },
        { min: 60, max: 160, target: 8 },
        { min: 160, max: 320, target: 10 },
        { min: 320, max: 650, target: 8 },
        { min: 650, max: 2500, target: 6 }
      );
    } else {
      const max = sunFilterConfig.maxDistanceKm;
      bands.push(
        { min: 0, max: max * 0.25, target: 6 },
        { min: max * 0.25, max: max * 0.6, target: 10 },
        { min: max * 0.6, max: max, target: 14 }
      );
    }

    const seenGrid = new Set<string>();
    const candidates: typeof radiusFiltered = [];

    for (const b of bands) {
      const inBand = radiusFiltered.filter(s => {
        const d = s.distance ?? 0;
        return d >= b.min && d <= b.max;
      });
      let addedInBand = 0;
      for (const s of inBand) {
        const gridKey = `${(s.location.latitude / 0.15).toFixed(0)},${(s.location.longitude / 0.25).toFixed(0)}`;
        if (!seenGrid.has(gridKey)) {
          seenGrid.add(gridKey);
          candidates.push(s);
          addedInBand++;
          if (addedInBand >= b.target) break;
        }
      }
    }

    const missingCoords: Coordinates[] = [];
    for (const c of candidates) {
      const k = coordKey(c.location);
      if (!forecastMap.has(k)) {
        missingCoords.push(c.location);
      }
    }

    if (missingCoords.length === 0) {
      setLoadingForecasts(false);
      return;
    }

    setLoadingForecasts(true);
    isFetchingForecastsRef.current = true;

    fetchForecastsForCoordinates(missingCoords, { days: 4, maxConcurrent: 8 })
      .then(newMap => {
        setForecastMap(prev => {
          const next = new Map(prev);
          for (const [k, v] of newMap.entries()) {
            next.set(k, v);
          }
          return next;
        });
      })
      .catch(e => {
        console.warn('Kunne ikke hente solvarsel for kandidater:', e);
      })
      .finally(() => {
        isFetchingForecastsRef.current = false;
        setLoadingForecasts(false);
      });
  }, [sunFilterConfig.enabled, sunFilterConfig.maxDistanceKm, effectiveUserPos?.lat, effectiveUserPos?.lon, allSpots?.length]);

  useEffect(() => {
    if (sidebarMode !== 'news') return;
    let cancelled = false;
    const load = async () => {
      setLoadingNews(true);
      try {
        const [spots, reviews] = await Promise.all([
          fetchRecentNewsSpots(10, enabledCountries),
          // Over-fetch so country filtering can still leave a populated list.
          fetchRecentReviews(30)
        ]);
        if (cancelled) return;
        setRecentNewsSpots(spots);
        // Filter reviews to spots in enabled countries when we know the spot's location
        // (either from the news feed itself or from allSpots already loaded by the map).
        const spotLocationById = new Map<string, { latitude: number; longitude: number }>();
        for (const s of spots) if (s.location) spotLocationById.set(s.id, s.location);
        for (const s of allSpots) if (s.location) spotLocationById.set(s.id, s.location);
        const reviewsFiltered = enabledCountries
          ? reviews.filter((r) => {
              const loc = r.spotId ? (spotLocationById.get(r.spotId) || r.spotLocation) : r.spotLocation;
              if (!loc) return true; // unknown location → keep, avoids hiding fresh activity
              return isSpotInEnabledCountry({ id: r.spotId, location: loc } as CampingSpot, enabledCountries);
            })
          : reviews;
        setRecentReviews(reviewsFiltered.slice(0, 10));
      } catch (e) {
        console.warn('Failed to load news feed', e);
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // allSpots intentionally not a dep — we only need a snapshot for review lookup
    // and don't want map panning to thrash the news fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarMode, enabledCountries]);

  useEffect(() => {
    if (recentNewsSpots.length === 0) return;
    let cancelled = false;
    const missing = recentNewsSpots.filter((spot) => !newsAddresses[spot.id]);
    if (missing.length === 0) return;

    // Fetch addresses one at a time with delay to respect Nominatim rate limits
    // and batch state updates to avoid per-item re-renders
    const loadAddresses = async () => {
      const results: [string, string][] = [];
      for (const spot of missing) {
        if (cancelled) break;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${spot.location.latitude}&lon=${spot.location.longitude}&zoom=12`;
          const res = await fetch(url, { headers: { 'Accept-Language': 'no' } });
          const data = await res.json();
          const addr = data?.address || {};
          const parts = [
            addr.municipality || addr.city || addr.town || addr.village || addr.hamlet,
            addr.county
          ].filter(Boolean);
          results.push([spot.id, parts.join(', ') || data.display_name || '']);
        } catch {
          results.push([spot.id, '']);
        }
        // Nominatim requires max 1 req/sec
        if (!cancelled) await new Promise(r => setTimeout(r, 1100));
      }

      if (cancelled) return;
      setNewsAddresses((prev) => {
        const next = { ...prev };
        for (const [id, value] of results) {
          if (value) next[id] = value;
        }
        try { localStorage.setItem('fricamp_news_addresses', JSON.stringify(next)); } catch {}
        return next;
      });
    };

    loadAddresses();
    return () => { cancelled = true; };
  }, [recentNewsSpots]);

  const toggleRegion = (region: RouteRegion) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      if (next.has(region)) next.delete(region);
      else next.add(region);
      return next;
    });
  };

  const NORWEGIAN_REGIONS: RouteRegion[] = ['Nord-Norge', 'Midt-Norge', 'Sør-Norge'];
  const NORDIC_COUNTRIES: { key: RouteRegion; flag: string; label: string; regions?: RouteRegion[] }[] = [
    { key: 'Norge' as RouteRegion, flag: '🇳🇴', label: 'Norge', regions: ['Nord-Norge', 'Midt-Norge', 'Sør-Norge'] },
    { key: 'Sverige', flag: '🇸🇪', label: 'Sverige', regions: ['Norrland', 'Svealand', 'Götaland'] },
    { key: 'Danmark', flag: '🇩🇰', label: 'Danmark' },
    { key: 'Finland', flag: '🇫🇮', label: 'Finland' },
    { key: 'Island', flag: '🇮🇸', label: 'Island' },
  ];

  const groupedRoutes = React.useMemo(() => {
    let displayedRoutes = showFavoritesOnly
      ? routes.filter(r => favoriteRouteIds.includes(r.id))
      : routes;

    if (routeFilter === 'mine' && user) {
      displayedRoutes = displayedRoutes.filter(r => r.createdBy === user.uid);
    }

    const groups: Record<string, GpxRoute[]> = {
      'Nord-Norge': [],
      'Midt-Norge': [],
      'Sør-Norge': [],
      'Norrland': [],
      'Svealand': [],
      'Götaland': [],
      'Sverige': [],
      'Danmark': [],
      'Finland': [],
      'Island': [],
      'Usortert': []
    };
    const REGION_COUNTRY: Record<string, string> = {
      'Nord-Norge': 'NO', 'Midt-Norge': 'NO', 'Sør-Norge': 'NO',
      'Norrland': 'SE', 'Svealand': 'SE', 'Götaland': 'SE', 'Sverige': 'SE',
      'Danmark': 'DK', 'Finland': 'FI', 'Island': 'IS',
    };
    const COUNTRY_FALLBACK: Record<string, string> = {
      'NO': 'Sør-Norge', 'SE': 'Götaland', 'DK': 'Danmark', 'FI': 'Finland',
    };

    displayedRoutes.forEach(r => {
      const coord = r.center || r.startCoords;
      const detectedCountry = coord ? detectCountry(coord.latitude, coord.longitude) : null;

      if (r.region && groups[r.region]) {
        // Verify region matches actual coordinates — if mismatch, reassign
        const regionCountry = REGION_COUNTRY[r.region];
        if (detectedCountry && regionCountry && detectedCountry !== regionCountry) {
          const fallback = COUNTRY_FALLBACK[detectedCountry];
          if (fallback && groups[fallback]) { groups[fallback].push(r); return; }
        }
        groups[r.region].push(r);
      } else if (detectedCountry) {
        const fallback = COUNTRY_FALLBACK[detectedCountry];
        if (fallback && groups[fallback]) { groups[fallback].push(r); return; }
        groups['Usortert'].push(r);
      } else {
        groups['Usortert'].push(r);
      }
    });
    return groups;
  }, [routes, showFavoritesOnly, favoriteRouteIds, routeFilter, user]);

  // Notify map of filtered preview route IDs when category/season filters change
  React.useEffect(() => {
    if (!previewRegion || (!regionCategoryFilter && seasonFilter === 'all')) {
      onPreviewFilteredRouteIds?.(null); // null = show all (no filter)
      return;
    }
    const regionRoutes = groupedRoutes[previewRegion] || [];
    let filtered = regionCategoryFilter
      ? regionRoutes.filter(r => r.suitableCategories?.includes(regionCategoryFilter as any))
      : regionRoutes;
    if (seasonFilter === 'year_round') filtered = filtered.filter(r => r.winterSuitability === 'good');
    else if (seasonFilter === 'summer_only') filtered = filtered.filter(r => r.winterSuitability === 'not_recommended');
    onPreviewFilteredRouteIds?.(new Set(filtered.map(r => String(r.id))));
  }, [previewRegion, regionCategoryFilter, seasonFilter, groupedRoutes, onPreviewFilteredRouteIds]);

  const hasMultipleCountries = React.useMemo(() => {
    const nordicCountryRegions = ['Norrland', 'Svealand', 'Götaland', 'Sverige', 'Danmark', 'Finland', 'Island'];
    return nordicCountryRegions.some(c => (groupedRoutes[c]?.length || 0) > 0);
  }, [groupedRoutes]);

  // Liste over alle hentede værpunkter for geografisk interpolasjon
  const fetchedForecastList = React.useMemo(() => {
    const list: { lat: number; lon: number; forecast: DailyWeather[] }[] = [];
    for (const [key, forecast] of forecastMap.entries()) {
      if (forecast && forecast.length > 0) {
        const [latStr, lonStr] = key.split(',');
        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);
        if (!isNaN(lat) && !isNaN(lon)) {
          list.push({ lat, lon, forecast });
        }
      }
    }
    return list;
  }, [forecastMap]);

  // Henter prognose for en plass: 1) direkte match, eller 2) nærmeste hentede værpunkt innen 65 km
  const getForecastForSpot = useCallback((spot: CampingSpot): DailyWeather[] | undefined => {
    if (spot.forecast && spot.forecast.length > 0) return spot.forecast;
    if (!spot.location) return undefined;
    const direct = forecastMap.get(coordKey(spot.location));
    if (direct && direct.length > 0) return direct;
    if (fetchedForecastList.length === 0) return undefined;

    let closestDist = Infinity;
    let closestForecast: DailyWeather[] | undefined;
    for (const item of fetchedForecastList) {
      const d = haversineKm(spot.location.latitude, spot.location.longitude, item.lat, item.lon);
      if (d < closestDist) {
        closestDist = d;
        closestForecast = item.forecast;
        if (d < 1) break;
      }
    }
    return closestDist <= 65 ? closestForecast : undefined;
  }, [forecastMap, fetchedForecastList]);

  const filteredSpots = React.useMemo(() => {
    let result = spots;

    if (showFavoritesOnly) {
      result = result.filter(s => favoriteSpotIds.includes(s.id) && s.isInvalid !== true);
    } else if (sunFilterConfig.enabled && effectiveUserPos) {
      // Ved soljakt: søk i allSpots (eller spots) innenfor radius, ikke bare synlig kartutsnitt
      const pool = (allSpots && allSpots.length > 0 ? allSpots : spots)
        .filter(s => s && s.location && !s.isInvalid);

      let radiusSpots = pool.map(s => {
        const forecast = getForecastForSpot(s);
        return {
          ...s,
          distance: haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude),
          forecast: forecast || s.forecast,
        };
      });

      if (sunFilterConfig.maxDistanceKm > 0) {
        radiusSpots = radiusSpots.filter(s => (s.distance ?? 9999) <= sunFilterConfig.maxDistanceKm);
      }

      radiusSpots.sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));

      if (forecastMap.size > 0 || !loadingForecasts) {
        radiusSpots = radiusSpots.filter(s => {
          if (!s.forecast || s.forecast.length === 0) {
            return false;
          }
          return isSunnyForecast(s.forecast, sunFilterConfig.period, sunFilterConfig.strictSun);
        });
      }

      result = radiusSpots;
    } else if (currentViewBounds) {
      result = result.filter(s =>
        s.location.latitude >= currentViewBounds.latMin &&
        s.location.latitude <= currentViewBounds.latMax &&
        s.location.longitude >= currentViewBounds.lonMin &&
        s.location.longitude <= currentViewBounds.lonMax
      );
    }

    // Keep selected spot visible in the list even if viewport changes during animated fitBounds.
    if (selectedSpotId) {
      const selected = spots.find(s => s.id === selectedSpotId) || allSpots.find(s => s.id === selectedSpotId);
      if (selected && !result.some(s => s.id === selectedSpotId)) {
        result = [selected, ...result];
      }
    }

    // Alltid koble på avstand og værprognose når brukerposisjon eller prognose er kjent
    if (!sunFilterConfig.enabled) {
      result = result.map(s => {
        const dist = effectiveUserPos && s.location
          ? haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude)
          : s.distance;
        const f = getForecastForSpot(s);
        return {
          ...s,
          ...(dist !== undefined ? { distance: dist } : {}),
          ...(f ? { forecast: f } : {}),
        };
      });

      if (sortByDistance && effectiveUserPos) {
        result.sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));
      } else if (sortByPopularity) {
        result.sort((a, b) => {
          const scoreA = (a.visitCount ?? 0) * 2 + (a.isVerified ? 5 : 0);
          const scoreB = (b.visitCount ?? 0) * 2 + (b.isVerified ? 5 : 0);
          return scoreB - scoreA;
        });
      }
    }

    return result;
  }, [spots, allSpots, currentViewBounds, showFavoritesOnly, selectedSpotId, favoriteSpotIds,
    sortByDistance, sortByPopularity, effectiveUserPos, sunFilterConfig, forecastMap, loadingForecasts, getForecastForSpot]);

  // Vis alltid filter- og solknapper i 'spots'-modus slik at brukeren alltid kan trykke «Finn sol» eller sortere
  const showFilterControls =
    sidebarMode === 'spots' ||
    filteredSpots.length > 2 ||
    (activeFilters?.size ?? 0) > 0 ||
    sortByDistance ||
    sortByPopularity ||
    sunFilterConfig.enabled;

  // Pre-compute nearby spots for the selected spot (avoids O(n²) in render loop)
  const nearbyForSelected = React.useMemo(() => {
    if (!selectedSpotId) return [];
    const selected = filteredSpots.find(s => s.id === selectedSpotId);
    if (!selected) return [];
    return filteredSpots
      .filter(s => s.id !== selectedSpotId)
      .map(s => ({
        ...s,
        distance: haversineKm(selected.location.latitude, selected.location.longitude, s.location.latitude, s.location.longitude),
      }))
      .sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999))
      .slice(0, 3);
  }, [filteredSpots, selectedSpotId]);

  // Proximity alert: check for nearby unvisited spots when GPS position updates
  // - Any unvisited spot within 500m (likely the user is at the location)
  // - Favorited unvisited spots within 2km (existing behavior)
  useEffect(() => {
    if (!sortByDistance || !userPos) {
      setNearbyFavorite(null);
      return;
    }
    const getDist = (s: CampingSpot) => s.distance ?? haversineKm(userPos.lat, userPos.lon, s.location.latitude, s.location.longitude);
    const nearby = filteredSpots.find(s =>
      !visitedSpotIds.includes(s.id) && getDist(s) <= 0.5
    ) || filteredSpots.find(s =>
      favoriteSpotIds.includes(s.id) &&
      !visitedSpotIds.includes(s.id) &&
      getDist(s) <= 2.0
    );
    if (nearby && nearby.id !== proximityDismissed) {
      setNearbyFavorite(nearby);
    } else if (!nearby) {
      setNearbyFavorite(null);
    }
  }, [sortByDistance, userPos, filteredSpots, favoriteSpotIds, visitedSpotIds, proximityDismissed]);

  useEffect(() => {
    if (selectedSpotId && sidebarMode === 'spots') {
      setTimeout(() => {
        const element = document.getElementById(`spot-card-${selectedSpotId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [selectedSpotId, sidebarMode]);

  const activeRouteDetail = draftRouteDetail || (selectedRouteId ? routes.find(r => r.id === selectedRouteId) : null);
  const isDraftRouteDetail = !!draftRouteDetail;

  useEffect(() => {
    if (sidebarMode !== 'routes' || !selectedRouteId || !user) return;
    const route = routes.find((r) => r.id === selectedRouteId);
    if (route?.createdBy === user.uid) {
      setRouteFilter('mine');
    }
  }, [routes, selectedRouteId, sidebarMode, user]);

  const strandFilterHelpText = t('sidebar.strand_filter_help');
  const spotMatchesQuickFilter = useCallback((spot: CampingSpot, filterId: string) => {
    const featureStrings = Array.isArray(spot.features) ? spot.features.map((f) => String(f).toUpperCase()) : [];
    const hasFeatureToken = (...tokens: string[]) =>
      featureStrings.some((ft) => tokens.some((token) => ft.includes(token)));

    if (filterId === 'fri_tilgang') return !spot.legal?.hasBarrier;
    if (filterId === 'verifisert') return !!spot.isVerified;
    if (filterId === 'strand') return spot.spotType === 'coastal' || hasFeatureToken('STRAND', 'KYST', 'SJØ', 'SJO', 'BEACH', 'COAST', 'FJORD');
    if (filterId === 'toalett') return !!spot.rasteplassMetadata?.toalett || hasFeatureToken('TOALETT', 'TOALETTER', 'TOILET', 'RESTROOM', 'WC');
    if (filterId === 'god_dekning') return spot.coverage === 'God' || hasFeatureToken('GOD DEKNING', 'GOOD COVERAGE', 'STERK DEKNING', '4G', '5G', 'FULL DEKNING');
    if (filterId === 'ikke_besøkt') return !visitedSpotIds.includes(spot.id);
    if (filterId === 'beste') return spot.ratingTier === 3;
    return true;
  }, [visitedSpotIds]);

  const filterChips = React.useMemo(() => {
    const chips = [
      { id: 'beste', emoji: '⭐', label: t('sidebar.filters.best', 'Beste'), tooltip: t('sidebar.filters.best_help', 'Kun de høyest vurderte plassene (topp stjerne-tier fra anmeldelser)') },
      { id: 'fri_tilgang', emoji: '🔓', label: t('sidebar.filters.free_access') },
      { id: 'verifisert', emoji: '✅', label: t('sidebar.filters.verified') },
      { id: 'strand', emoji: '🌊', label: t('sidebar.filters.strand'), tooltip: strandFilterHelpText },
      { id: 'toalett', emoji: '🚽', label: t('sidebar.filters.toilet') },
      ...(user ? [{ id: 'ikke_besøkt', emoji: '👁️', label: t('sidebar.filters.not_visited') }] : []),
    ];

    return chips;
  }, [user, strandFilterHelpText]);
  const filterChipCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const chip of filterChips) {
      counts[chip.id] = filteredSpots.reduce((sum, spot) => sum + (spotMatchesQuickFilter(spot, chip.id) ? 1 : 0), 0);
    }
    return counts;
  }, [filterChips, filteredSpots, spotMatchesQuickFilter]);

  const totalActiveFilterCount = React.useMemo(() => {
    return (activeFilters?.size || 0) + (sunFilterConfig.enabled ? 1 : 0) + (sortByDistance || sortByPopularity ? 1 : 0);
  }, [activeFilters, sunFilterConfig.enabled, sortByDistance, sortByPopularity]);

  // Nærmeste fricamp uavhengig av kartutsnitt og filter
  const nearestAnySpotInfo = React.useMemo(() => {
    if (!effectiveUserPos) return null;
    const pool = (allSpots && allSpots.length > 0 ? allSpots : spots)
      .filter(s => s && s.location && !s.isInvalid);
    if (pool.length === 0) return null;

    let closest: CampingSpot | null = null;
    let minDist = Infinity;

    for (const s of pool) {
      const d = haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude);
      if (d < minDist) {
        minDist = d;
        closest = s;
      }
    }

    if (!closest || !Number.isFinite(minDist)) return null;

    const km = minDist;
    const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    const etaMin = Math.max(1, Math.round(km * 1.5 / 70 * 60));
    const etaLabel = etaMin < 60 ? `~${etaMin} min` : `~${Math.floor(etaMin / 60)}t ${String(etaMin % 60).padStart(2, '0')}m`;

    return {
      spot: closest,
      distanceKm: km,
      distLabel,
      etaLabel,
    };
  }, [allSpots, spots, effectiveUserPos]);

  // Nærmeste med aktive filtre (uavhengig av kartutsnitt)
  const nearestFilteredSpotInfo = React.useMemo(() => {
    if (!effectiveUserPos || !activeFilters || activeFilters.size === 0) return null;
    const pool = (allSpots && allSpots.length > 0 ? allSpots : spots)
      .filter(s => s && s.location && !s.isInvalid && Array.from(activeFilters).every(fId => spotMatchesQuickFilter(s, fId)));
    if (pool.length === 0) return null;

    let closest: CampingSpot | null = null;
    let minDist = Infinity;

    for (const s of pool) {
      const d = haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude);
      if (d < minDist) {
        minDist = d;
        closest = s;
      }
    }

    if (!closest || !Number.isFinite(minDist)) return null;

    const km = minDist;
    const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    const etaMin = Math.max(1, Math.round(km * 1.5 / 70 * 60));
    const etaLabel = etaMin < 60 ? `~${etaMin} min` : `~${Math.floor(etaMin / 60)}t ${String(etaMin % 60).padStart(2, '0')}m`;

    return {
      spot: closest,
      distanceKm: km,
      distLabel,
      etaLabel,
    };
  }, [allSpots, spots, effectiveUserPos, activeFilters, spotMatchesQuickFilter]);

  // Nærmeste plass med sol innenfor søkeradius når solfilteret er aktivt
  const nearestSunSpotInfo = React.useMemo(() => {
    if (!effectiveUserPos || !sunFilterConfig.enabled) return null;
    const pool = (allSpots && allSpots.length > 0 ? allSpots : spots)
      .filter(s => s && s.location && !s.isInvalid);
    if (pool.length === 0) return null;

    let closest: CampingSpot | null = null;
    let minDist = Infinity;

    for (const s of pool) {
      const d = haversineKm(effectiveUserPos.lat, effectiveUserPos.lon, s.location.latitude, s.location.longitude);
      if (sunFilterConfig.maxDistanceKm > 0 && d > sunFilterConfig.maxDistanceKm) continue;

      const forecast = getForecastForSpot(s);
      if (forecast && forecast.length > 0 && isSunnyForecast(forecast, sunFilterConfig.period, sunFilterConfig.strictSun)) {
        if (d < minDist) {
          minDist = d;
          closest = {
            ...s,
            distance: d,
            forecast,
          };
        }
      }
    }

    if (!closest || !Number.isFinite(minDist)) return null;

    const km = minDist;
    const distLabel = km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
    const etaMin = Math.max(1, Math.round(km * 1.5 / 70 * 60));
    const etaLabel = etaMin < 60 ? `~${etaMin} min` : `~${Math.floor(etaMin / 60)}t ${String(etaMin % 60).padStart(2, '0')}m`;

    return {
      spot: closest,
      distanceKm: km,
      distLabel,
      etaLabel,
    };
  }, [allSpots, spots, effectiveUserPos, sunFilterConfig, getForecastForSpot]);

  const handleShowNearestSpot = (targetSpot: CampingSpot) => {
    onSelectSpot(targetSpot.id);
    if (onFitBounds && targetSpot.location) {
      if (effectiveUserPos) {
        const lat1 = effectiveUserPos.lat;
        const lon1 = effectiveUserPos.lon;
        const lat2 = targetSpot.location.latitude;
        const lon2 = targetSpot.location.longitude;
        const padLat = Math.max(Math.abs(lat2 - lat1) * 0.25, 0.015);
        const padLon = Math.max(Math.abs(lon2 - lon1) * 0.25, 0.015);
        onFitBounds([
          [Math.min(lat1, lat2) - padLat, Math.min(lon1, lon2) - padLon],
          [Math.max(lat1, lat2) + padLat, Math.max(lon1, lon2) + padLon]
        ]);
      } else {
        const lat = targetSpot.location.latitude;
        const lon = targetSpot.location.longitude;
        onFitBounds([[lat - 0.02, lon - 0.02], [lat + 0.02, lon + 0.02]]);
      }
    }
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg-surface-alt)] relative pb-24 md:pb-0">
      <div className="px-3 py-2 md:p-4 border-b border-[var(--border-primary)] bg-[var(--bg-surface-alt)] z-10 shrink-0">
        <div className="flex items-center justify-between mb-1 md:mb-4">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h2 className="text-xs md:text-sm font-black uppercase tracking-widest text-brand">
                {isDraftRouteDetail
                  ? 'Planlagt rute'
                  : sidebarMode === 'discover'
                    ? t('nav.discover')
                    : sidebarMode === 'news'
                      ? t('sidebar.news')
                      : sidebarMode === 'routes'
                        ? (activeRouteDetail ? t('sidebar.selected_route') : t('sidebar.routes_overview'))
                        : (sidebarMode === 'favorites' ? t('sidebar.your_favorites') : t('sidebar.explore_area'))}
              </h2>
              {!isDraftRouteDetail && sidebarMode === 'routes' && user?.activeVehicleCategory && onToggleRouteVehicleFilter && (
                <button
                  onClick={onToggleRouteVehicleFilter}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest transition whitespace-nowrap ${
                    !routeVehicleFilterOff
                      ? 'bg-brand/15 text-emerald-400 border border-brand/30 hover:bg-brand/25'
                      : 'bg-[var(--bg-input)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:bg-[var(--bg-surface)]'
                  }`}
                >
                  <Filter size={8} />
                  {!routeVehicleFilterOff ? t('routes.filtered_for_vehicle', 'Tilpasset ditt kjøretøy') : t('routes.showing_all', 'Viser alle ruter')}
                  {!routeVehicleFilterOff && <X size={8} className="opacity-60" />}
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <p className="text-tiny text-[var(--text-muted)] font-bold uppercase tracking-widest whitespace-nowrap">
                {isDraftRouteDetail
                  ? `${spots.length} steder langs ruten`
                  : sidebarMode === 'routes'
                  ? (activeRouteDetail ? t('sidebar.detailed_view') : `${Object.values(groupedRoutes).reduce((a: number, b: GpxRoute[]) => a + b.length, 0)} ${t('nav.routes').toLowerCase()}`)
                  : sunFilterConfig.enabled
                  ? (loadingForecasts
                      ? 'Søker etter sol i området...'
                      : nearestSunSpotInfo
                        ? `${filteredSpots.length} ${filteredSpots.length === 1 ? 'plass' : 'plasser'} med sol · Nærmeste er ${nearestSunSpotInfo.distLabel} unna`
                        : `Ingen plasser med sol funnet innen ${sunFilterConfig.maxDistanceKm > 0 ? `${sunFilterConfig.maxDistanceKm} km` : 'rekkevidde'}`)
                  : filteredSpots.length === 0 && nearestAnySpotInfo
                  ? `0 steder synlig · Nærmeste er ${nearestAnySpotInfo.distLabel} unna`
                  : t('search.results_visible', { count: filteredSpots.length })
                }
              </p>
              {activeRouteIds.size > 0 && (
                <button
                  onClick={() => setActiveRouteIds(new Set())}
                  className="flex items-center gap-1.5 px-2 py-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-md text-tiny font-black uppercase tracking-widest transition"
                >
                  <RefreshCcw size={10} /> {t('sidebar.reset_routes')}
                </button>
              )}
              {sidebarMode === 'routes' && !activeRouteDetail && (
                <div className="relative">
                  <button
                    onClick={() => setShowRouteFilterDropdown(!showRouteFilterDropdown)}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-tiny font-black uppercase tracking-widest transition ${
                      regionCategoryFilter || seasonFilter !== 'all'
                        ? 'bg-brand/15 text-emerald-400 border border-brand/30'
                        : 'bg-[var(--bg-input)] text-[var(--text-muted)] border border-[var(--border-primary)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    <Filter size={10} />
                    {regionCategoryFilter ? (DRILLDOWN_CAT[regionCategoryFilter]?.emoji || '') + ' ' + (DRILLDOWN_CAT[regionCategoryFilter]?.label || regionCategoryFilter) : seasonFilter !== 'all' ? (seasonFilter === 'year_round' ? '❄️' : '☀️') : t('routes.filter', 'Filter')}
                    <ChevronDown size={10} className={`transition-transform ${showRouteFilterDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  {showRouteFilterDropdown && (
                    <>
                      <div className="fixed inset-0 z-[50]" onClick={() => setShowRouteFilterDropdown(false)} />
                      <div className="absolute left-0 top-full mt-1 z-[51] bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.5)] overflow-hidden min-w-[200px]">
                        <div className="px-3 pt-2.5 pb-1">
                          <span className="text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('routes.vehicle_type', 'Kjøretøytype')}</span>
                        </div>
                        <button onClick={() => { setRegionCategoryFilter(null); }} className={`w-full px-3 py-2 text-left text-tiny font-bold transition-colors ${!regionCategoryFilter ? 'text-emerald-400 bg-brand/10' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}>
                          {t('routes.all_types', 'Alle typer')}
                        </button>
                        {Object.entries(DRILLDOWN_CAT).map(([key, meta]) => (
                          <button key={key} onClick={() => { setRegionCategoryFilter(regionCategoryFilter === key ? null : key); }} className={`w-full px-3 py-2 text-left text-tiny font-bold transition-colors ${regionCategoryFilter === key ? 'text-emerald-400 bg-brand/10' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}>
                            {meta.emoji} {meta.label}
                          </button>
                        ))}
                        <div className="border-t border-[var(--border-primary)] px-3 pt-2.5 pb-1 mt-1">
                          <span className="text-4xs font-black text-[var(--text-muted)] uppercase tracking-widest">{t('routes.season', 'Sesong')}</span>
                        </div>
                        <button onClick={() => setSeasonFilter('all')} className={`w-full px-3 py-2 text-left text-tiny font-bold transition-colors ${seasonFilter === 'all' ? 'text-emerald-400 bg-brand/10' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}>
                          {t('routes.filter_all_seasons', 'Alle sesonger')}
                        </button>
                        <button onClick={() => setSeasonFilter(seasonFilter === 'year_round' ? 'all' : 'year_round')} className={`w-full px-3 py-2 text-left text-tiny font-bold transition-colors ${seasonFilter === 'year_round' ? 'text-blue-400 bg-blue-500/10' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}>
                          ❄️ {t('route.seasonal_all_year')}
                        </button>
                        <button onClick={() => { setSeasonFilter(seasonFilter === 'summer_only' ? 'all' : 'summer_only'); }} className={`w-full px-3 py-2 text-left text-tiny font-bold transition-colors ${seasonFilter === 'summer_only' ? 'text-amber-400 bg-amber-500/10' : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'}`}>
                          ☀️ {t('route.seasonal_summer_route')}
                        </button>
                        {(regionCategoryFilter || seasonFilter !== 'all') && (
                          <button onClick={() => { setRegionCategoryFilter(null); setSeasonFilter('all'); setShowRouteFilterDropdown(false); }} className="w-full px-3 py-2 text-left text-tiny font-bold text-red-400 hover:bg-red-500/10 border-t border-[var(--border-primary)]">
                            <RefreshCcw size={10} className="inline mr-1.5" />{t('routes.clear_filters', 'Nullstill filter')}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-2 md:pr-10">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onShowOnMap) onShowOnMap();
              }}
              className="md:hidden p-2 -mr-1 rounded-xl bg-[var(--bg-input)] text-[var(--text-primary)] border border-[var(--border-secondary)] transition active:scale-95 shadow-lg flex items-center justify-center z-[3100] cursor-pointer"
              title={t('common.close_to_map')}
            >
              <X size={18} strokeWidth={3} />
            </button>
          </div>
        </div>

        {isSearchActive && (
          <div className="bg-brand/10 border border-brand/20 p-3 rounded-xl flex items-center justify-between mb-2">
            <span className="text-tiny font-black text-emerald-400 uppercase tracking-widest">{t('sidebar.results_active')}</span>
            <button onClick={onClearSearch} aria-label={t('common.close', 'Lukk')} className="p-2 -mr-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"><X size={16} /></button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden relative">
        {showFollowSummary && followSummary && (
          <div className="px-4 pt-3">
            <div className="rounded-2xl border border-brand/25 bg-brand/10 px-3 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-3xs font-black uppercase tracking-widest text-emerald-300">
                    {followMode === 'route_follow' ? 'Kjorepanel' : 'GPS-folging'}
                  </div>
                  <div className="text-sm font-black text-[var(--text-primary)] truncate mt-1">
                    {followMode === 'route_follow' ? (followSummary.routeName || 'Aktiv rute') : 'Kart folger bilen'}
                  </div>
                </div>
                {followSummary.routeDistanceM !== null && followMode === 'route_follow' && (
                  <div className="text-2xs font-black text-emerald-200 whitespace-nowrap">
                    {Math.round(followSummary.routeDistanceM)} m fra rute
                  </div>
                )}
              </div>
              <div className="mt-3 rounded-xl border border-white/10 bg-[var(--bg-surface)]/80 px-3 py-2.5">
                <div className="text-3xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t("auto.spotlist.aktuelt_punkt", "Aktuelt punkt")}</div>
                <div className="text-sm font-black text-[var(--text-primary)] truncate mt-1">
                  {followSummary.activeTargetName || 'Ingen punkt i narhet'}
                </div>
                <div className="text-2xs text-[var(--text-secondary)] mt-1">
                  {followSummary.activeTargetDistanceM !== null
                    ? `${Math.round(followSummary.activeTargetDistanceM)} m unna`
                    : 'Ingen valgt markering'}
                </div>
              </div>
            </div>
          </div>
        )}
        {(sidebarMode === 'routes' || isDraftRouteDetail) && (
          activeRouteDetail ? (
            <>
              <RouteDetailView
                route={activeRouteDetail}
                onClose={() => {
                  if (isDraftRouteDetail) {
                    onCloseDraftRouteDetail?.();
                    return;
                  }
                  onCloseRouteDetails?.();
                  onShowOnMap?.();
                }}
                onEdit={() => {
                  if (!isDraftRouteDetail) onEditRoute(activeRouteDetail);
                }}
                onShowMap={() => {
                  if (isDraftRouteDetail) {
                    onShowOnMap?.();
                    return;
                  }
                  onShowOnMap?.();
                }}
                user={user}
                onReanalyze={isDraftRouteDetail ? undefined : onReanalyzeRoute}
                onReanalyzeLayer={isDraftRouteDetail ? undefined : onReanalyzeRouteLayer}
                isBackgroundAnalyzing={analyzingRouteIds.has(activeRouteDetail.id)}
                isLoading={isLoading}
                routeFacilityLoading={routeFacilityLoading}
                isFavorite={!isDraftRouteDetail && favoriteRouteIds.includes(activeRouteDetail.id)}
                onToggleFavorite={isDraftRouteDetail ? undefined : () => onToggleFavoriteRoute?.(activeRouteDetail.id)}
                onOpenAuth={onOpenAuth}
                onRouteStatusChange={onRouteStatusChange}
                isPaidUser={isPaidUser}
                onOpenUpgrade={onOpenUpgrade}
                onAddRouteToMap={isDraftRouteDetail ? undefined : onAddRouteToMap}
                hasCollaborativeMaps={(collaborativeMaps?.length || 0) > 0}
                onStartFollowRoute={isDraftRouteDetail ? undefined : onStartFollowRoute}
                onNavigateToRoute={isDraftRouteDetail ? undefined : onNavigateToRoute}
                trackingStatus={trackingStatus}
                isDraftRoute={isDraftRouteDetail}
                routePath={isDraftRouteDetail ? draftRoutePath : null}
                onRouteWebcamsChange={onRouteWebcamsChange}
              />
              {overlaySpot && (
                <div className="absolute inset-0 z-20 bg-[var(--bg-surface-alt)] overflow-y-auto custom-scrollbar">
                  <div className="sticky top-0 z-10 flex items-center justify-between p-3 bg-[var(--bg-surface-alt)] border-b border-[var(--border-primary)]">
                    <button
                      onClick={onCloseOverlaySpot}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full text-2xs font-black uppercase tracking-wide border border-brand/35 bg-brand/10 text-emerald-300 hover:bg-brand/15 transition"
                    >
                      <ArrowLeft size={11} />
                      {t("auto.spotlist.tilbake_til_rute", "Tilbake til rute")}
                    </button>
                    <button
                      onClick={onCloseOverlaySpot}
                      className="p-2 rounded-xl text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-input)] transition"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <SpotCard
                    spot={overlaySpot}
                    isSelected={true}
                    activeHikingTrailId={activeHikingTrailId}
                    isFavorite={favoriteSpotIds.includes(overlaySpot.id)}
                    isVisited={visitedSpotIds.includes(overlaySpot.id)}
                    onSelect={() => {}}
                    onSelectHikingTrail={onSelectHikingTrail}
                    onRemoveSpot={onRemoveSpot}
                    onToggleFavorite={(e, id) => onToggleFavoriteSpot?.(e, id)}
                    onToggleVisit={(e, id) => onToggleVisit?.(e, id)}
                    onAddPastStay={onAddPastStay}
                    onDeletePastStay={onDeletePastStay}
                    onMarkOccupied={onMarkOccupied}
                    onExtendOccupancy={onExtendOccupancy}
                    onClearOccupancy={onClearOccupancy}
                    userPosition={userPosition}
                    onAddToMap={(e, id) => onAddToMap?.(e, id)}
                    onReanalyze={onReanalyze}
                    onEdit={onEditSpot}
                    user={user}
                    onOpenAuth={onOpenAuth}
                    isAnalyzing={activeAnalysisIds.has(overlaySpot.id)}
                    isGeneratingMeta={generatingMetaIds.has(overlaySpot.id)}
                    onFitBounds={onFitBounds}
                    collaborativeMaps={collaborativeMaps}
                    onSaveSearchResult={onSaveSearchResult}
                    onCreateRouteFromSpot={onCreateRouteFromSpot}
                    isCreatingRoute={creatingRouteSpotId === overlaySpot.id}
                    onShowNearestBarrier={onShowNearestBarrier}
                    onEnsureSpotPersisted={onEnsureSpotPersisted}
                    onNavigateInApp={onNavigateInApp}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col h-full bg-[var(--bg-surface-alt)] text-[var(--text-primary)]">
              {sidebarMode === 'routes' && (
                <div className="px-4 pb-3 flex items-center gap-2">
                  {user && (
                    <div className="flex bg-[var(--bg-input)] rounded-xl p-0.5 border border-[var(--border-primary)]">
                      <button
                        onClick={() => setRouteFilter('all')}
                        className={`px-3 py-2 rounded-lg text-3xs font-black uppercase tracking-widest transition-colors whitespace-nowrap ${routeFilter === 'all' ? 'bg-brand-dark text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                      >
                        {t('routes.all_routes')}
                      </button>
                      <button
                        onClick={() => setRouteFilter('mine')}
                        className={`px-3 py-2 rounded-lg text-3xs font-black uppercase tracking-widest transition-colors whitespace-nowrap ${routeFilter === 'mine' ? 'bg-brand-dark text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
                      >
                        {t('routes.my_routes')}
                      </button>
                    </div>
                  )}
                  <div className="flex gap-1.5 ml-auto">
                    {user && onUploadRoute && (
                      <button onClick={onUploadRoute} className="h-9 px-3 bg-brand-dark hover:bg-brand rounded-xl text-white flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest transition-colors">
                        <Plus size={12} /> {t('routes.upload_new')}
                      </button>
                    )}
                    <button onClick={onRefreshRoutes} className="h-9 px-3 hover:bg-[var(--bg-surface)] rounded-xl text-[var(--text-muted)] hover:text-[var(--text-secondary)] flex items-center gap-1.5 text-3xs font-black uppercase tracking-widest transition-colors">
                      <RefreshCcw size={12} />
                    </button>
                  </div>
                </div>
              )}

              {routeFilter === 'mine' && user && Object.keys(groupedRoutes).every(r => groupedRoutes[r].length === 0) && (
                <div className="flex flex-col items-center justify-center py-10 opacity-50">
                  <Route size={24} className="text-[var(--text-muted)] mb-2" />
                  <p className="text-tiny font-black uppercase text-[var(--text-muted)]">{t('routes.no_own_routes')}</p>
                  {onUploadRoute && (
                    <button onClick={onUploadRoute} className="mt-4 px-4 py-2 bg-brand-dark hover:bg-brand rounded-xl text-white text-tiny font-black uppercase tracking-widest flex items-center gap-2 transition-colors">
                      <Plus size={14} /> {t('routes.upload_new')}
                    </button>
                  )}
                </div>
              )}

              {showFavoritesOnly && routeFilter === 'all' && Object.keys(groupedRoutes).every(r => groupedRoutes[r].length === 0) && (
                <div className="flex flex-col items-center justify-center py-10 opacity-50">
                  <Heart size={24} className="text-[var(--text-muted)] mb-2" />
                  <p className="text-tiny font-black uppercase text-[var(--text-muted)]">{t('sidebar.no_favorite_routes')}</p>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 pt-0 space-y-4" style={{ overscrollBehavior: 'contain' }}>
                {(() => {
                  // Render a region block (reused by both flat and country modes)
                  const renderRegionBlock = (region: RouteRegion, regionRoutes: GpxRoute[], nested = false) => {
                    if (regionRoutes.length === 0) return null;
                    const isExpanded = expandedRegions.has(region);
                    const isRegionActive = previewRegion === region;

                    return (
                      <div key={region} className={nested ? 'border border-[var(--border-primary)] rounded-xl overflow-hidden' : 'bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-primary)] overflow-hidden'}>
                        <div className={`flex items-center justify-between p-4 hover:bg-[var(--bg-surface)] ${isRegionActive ? 'bg-emerald-900/20' : ''}`}>
                          <button
                            type="button"
                            className="flex-1 min-w-0 flex items-center gap-3 cursor-pointer text-left"
                            onClick={() => {
                              if (isRegionActive) setPreviewRegion(null);
                              else setPreviewRegion(region);
                              toggleRegion(region);
                            }}
                          >
                            <span className="text-2xs font-black uppercase tracking-widest text-emerald-400">{t(`regions.${region}`)}</span>
                            <span className="text-3xs font-bold text-[var(--text-muted)] bg-[var(--bg-input)] px-2 py-0.5 rounded-full">{regionRoutes.length}</span>
                            {isRegionActive && isRegionLoading && <Loader2 size={12} className="animate-spin text-emerald-400" />}
                          </button>
                          <button
                            type="button"
                            className="p-1 rounded-md text-[var(--text-muted)] hover:bg-[var(--bg-surface)] transition-colors"
                            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); setPreviewRegion(null); toggleRegion(region); }}
                            aria-label={isExpanded ? t('route.hide_region') : t('route.show_region')}
                          >
                            <ChevronDown size={14} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                      </div>

                      {isExpanded && (() => {
                        let filteredRegionRoutes = regionCategoryFilter
                          ? regionRoutes.filter(r => r.suitableCategories?.includes(regionCategoryFilter as any))
                          : regionRoutes;
                        if (seasonFilter === 'year_round') filteredRegionRoutes = filteredRegionRoutes.filter(r => r.winterSuitability === 'good');
                        else if (seasonFilter === 'summer_only') filteredRegionRoutes = filteredRegionRoutes.filter(r => r.winterSuitability === 'not_recommended');

                        return (
                        <div className="p-2 space-y-2 bg-[var(--bg-surface-alt)]">
                          {filteredRegionRoutes.length === 0 && (regionCategoryFilter || seasonFilter !== 'all') && (
                            <p className="text-2xs text-[var(--text-muted)] text-center py-3">{t('routes.no_matches_filter', 'Ingen ruter matcher filteret')}</p>
                          )}
                          {filteredRegionRoutes.map((route, idx) => {
                            const isActive = activeRouteIds.has(route.id);
                            const isAnalyzingThis = analyzingRouteIds.has(route.id);
                            const isFav = favoriteRouteIds.includes(route.id);
                            const isOwnRoute = user && route.createdBy === user.uid;
                            const routeColor = ROUTE_COLOR_PALETTE[idx % ROUTE_COLOR_PALETTE.length];

	                            return (
	                              <div
	                                key={route.id}
	                                onClick={() => {
	                                  if (onOpenRouteDetails) {
	                                    onOpenRouteDetails(route.id);
	                                    return;
	                                  }
	                                  onToggleRoute(route);
	                                }}
	                                className={`p-3 rounded-xl border cursor-pointer transition ${isActive ? 'bg-brand-dark/10 border-brand/50'
                                    : isOwnRoute && route.status === 'private' ? 'bg-purple-500/5 border-purple-500/20 hover:bg-purple-500/10'
                                      : isOwnRoute && route.status === 'pending' ? 'bg-amber-500/5 border-amber-500/20 hover:bg-amber-500/10'
                                        : 'bg-transparent border-transparent hover:bg-[var(--bg-surface)]'
                                  }`}
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 pr-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: routeColor }} />
                                      <h4 className={`text-xs font-black uppercase tracking-tight ${isActive ? 'text-emerald-400' : 'text-[var(--text-secondary)]'}`}>{route.name}</h4>
                                      {/* Personal vehicle suitability badge */}
                                      {user?.activeVehicleCategory && route.suitableCategories?.length ? (() => {
                                        const catMatch = route.suitableCategories.includes(user.activeVehicleCategory!) ||
                                          (user.activeVehicleEquipment?.four_wheel_drive && route.suitableCategories.includes('offroad'));
                                        const tooLong = user.activeVehicleLength && route.maxRecommendedLengthCm && user.activeVehicleLength > route.maxRecommendedLengthCm;
                                        const needs4wd = route.requires4wd && !user.activeVehicleEquipment?.four_wheel_drive;
                                        if (catMatch && !tooLong && !needs4wd) {
                                          return <span className="text-3xs px-1.5 py-0.5 rounded bg-brand/15 text-emerald-400 font-bold" title={t('routes.suits_your_vehicle', 'Passer ditt kjøretøy')}>✓ {t('routes.suitable_short', 'Egnet')}</span>;
                                        } else if (catMatch && (tooLong || needs4wd)) {
                                          return <span className="text-3xs px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-bold" title={tooLong ? `Maks ${(route.maxRecommendedLengthCm! / 100).toFixed(1)}m — ditt: ${(user.activeVehicleLength! / 100).toFixed(1)}m` : t('routes.requires_4wd', 'Krever 4WD')}>⚠ {tooLong ? `${(route.maxRecommendedLengthCm! / 100).toFixed(1)}m` : '4WD'}</span>;
                                        } else {
                                          return <span className="text-3xs px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-bold" title={t('routes.not_suited', 'Ikke anbefalt for ditt kjøretøy')}>✗</span>;
                                        }
                                      })() : null}
                                      {/* Contextual season indicator */}
                                      {(() => {
                                        const month = new Date().getMonth();
                                        const currentSeason = month >= 2 && month <= 4 ? 'spring' : month >= 5 && month <= 7 ? 'summer' : month >= 8 && month <= 10 ? 'autumn' : 'winter';
                                        const isWinter = currentSeason === 'winter' || currentSeason === 'spring'; // Dec-Apr counts as potential winter
                                        if (isWinter && route.winterSuitability === 'not_recommended') return <span className="text-4xs px-1 py-0.5 rounded bg-red-500/15 text-red-400 font-bold whitespace-nowrap">❄️✗</span>;
                                        if (route.bestSeasons?.length && route.bestSeasons.includes(currentSeason)) return <span className="text-4xs px-1 py-0.5 rounded bg-brand/15 text-emerald-400 whitespace-nowrap" title={t('routes.good_season_now', 'God sesong nå')}>🟢</span>;
                                        return null;
                                      })()}
                                      {route.winterSuitability === 'not_recommended' && <AlertTriangle size={10} className="text-amber-500 flex-shrink-0" />}
                                      {!isPaidUser && <Lock size={10} className="text-amber-500" />}
                                      {route.status === 'private' && <span className="flex items-center gap-0.5 text-4xs font-black uppercase text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full"><Lock size={8} /> {t('routes.status_private')}</span>}
                                      {route.status === 'pending' && <span className="flex items-center gap-0.5 text-4xs font-black uppercase text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded-full"><Clock size={8} /> {t('routes.status_pending')}</span>}
                                      {isOwnRoute && route.status === 'public' && <span className="text-4xs font-black uppercase text-emerald-400 bg-brand/10 px-1.5 py-0.5 rounded-full">{t('routes.your_route')}</span>}
                                      {isAnalyzingThis && <Loader2 size={10} className="animate-spin text-brand" />}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                      {route.distance && (
                                        <span className="flex items-center gap-1 text-2xs font-black text-[var(--text-secondary)] bg-[var(--bg-surface)] px-1.5 py-0.5 rounded border border-[var(--border-primary)] whitespace-nowrap">
                                          <MapPin size={10} /> {route.distance.toFixed(1)} km
                                        </span>
                                      )}
                                      {route.estimatedTime && (
                                        <span className="flex items-center gap-1 text-2xs font-black text-[var(--text-secondary)] bg-[var(--bg-surface)] px-1.5 py-0.5 rounded border border-[var(--border-primary)] whitespace-nowrap">
                                          <Clock size={10} /> {route.estimatedTime}
                                        </span>
                                      )}
                                      {route.view_count != null && route.view_count > 0 && (
                                        <span className="flex items-center gap-1 text-2xs font-black text-[var(--text-muted)] bg-[var(--bg-surface)] px-1.5 py-0.5 rounded border border-[var(--border-primary)] whitespace-nowrap">
                                          <Eye size={10} /> {route.view_count}
                                        </span>
                                      )}
                                      <div className="flex items-center gap-1.5 pl-1 border-l border-[var(--border-secondary)] ml-1">
                                        {route.createdByPhoto ? (
                                          <img src={route.createdByPhoto} className="w-5 h-5 rounded-full object-cover border border-[var(--border-secondary)]" alt="" />
                                        ) : (
                                          <User size={12} className="text-[var(--text-muted)]" />
                                        )}
                                        <span className="text-2xs text-[var(--text-muted)] font-bold truncate max-w-[80px]">{route.createdByName || t('route.anonymous')}</span>
                                      </div>
                                    </div>
                                    <p className="text-2xs text-[var(--text-muted)] mt-1 line-clamp-1">{route.description}</p>
                                    {(route.terrainTypes?.length || route.requires4wd) && (
                                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                                        {route.terrainTypes?.map(tt => {
                                          const TERRAIN_EMOJI: Record<string, string> = { paved: '🛣️', gravel: '🪨', forest: '🌲', sand: '🏖️', snow: '❄️', mountain: '⛰️', coastal: '🌊' };
                                          return <span key={tt} className="text-3xs px-1.5 py-0.5 rounded-full bg-[var(--bg-input)] text-[var(--text-muted)] border border-[var(--border-primary)]">{TERRAIN_EMOJI[tt] || ''} {tt}</span>;
                                        })}
                                        {route.requires4wd && <span className="text-3xs px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 font-bold">4WD</span>}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-1 shrink-0">
                                    {onToggleFavoriteRoute && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); if (!user) onOpenAuth(); else onToggleFavoriteRoute(route.id); }}
                                        className={`p-1.5 rounded transition-colors ${isFav ? 'text-red-500' : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'}`}
                                      >
                                        <Heart size={14} fill={isFav ? "currentColor" : "none"} />
                                      </button>
                                    )}
                                    {onOpenRouteDetails && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); onOpenRouteDetails(route.id); }}
                                        className="p-1.5 hover:bg-[var(--bg-surface)] rounded text-[var(--text-muted)] hover:text-emerald-400 transition-colors"
                                        title={t('sidebar.detailed_view')}
                                      >
                                        <Info size={14} />
                                      </button>
                                    )}
                                    {user && route.createdBy === user.uid && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); onEditRoute(route); }}
                                        className="p-1.5 hover:bg-[var(--bg-surface)] rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                                      >
                                        <Edit3 size={14} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        );
                      })()}
                    </div>
                    );
                  };

                  const unsortedBlock = (groupedRoutes['Usortert']?.length || 0) > 0
                    ? renderRegionBlock('Usortert' as RouteRegion, groupedRoutes['Usortert'] || [])
                    : null;

                  if (!hasMultipleCountries) {
                    // Single-country mode: flat Norwegian regions
                    return (
                      <>
                        {(NORWEGIAN_REGIONS as RouteRegion[]).map(region =>
                          renderRegionBlock(region, groupedRoutes[region] || [])
                        )}
                        {unsortedBlock}
                      </>
                    );
                  }

                  // Multi-country mode: country → region hierarchy
                  return (
                    <>
                      {NORDIC_COUNTRIES.map(country => {
                        const countryRoutes = country.regions
                          ? [...country.regions.flatMap(r => groupedRoutes[r] || []), ...(groupedRoutes[country.key] || [])]
                          : groupedRoutes[country.key] || [];
                        if (countryRoutes.length === 0) return null;
                        const isCountryExpanded = expandedCountries.has(country.label);

                        return (
                          <div key={country.key} className="bg-[var(--bg-surface)] rounded-2xl border border-[var(--border-primary)] overflow-hidden">
                            <button
                              type="button"
                              className="w-full flex items-center justify-between p-4 hover:bg-[var(--bg-surface)]"
                              onClick={() => setExpandedCountries(prev => {
                                const next = new Set(prev);
                                if (next.has(country.label)) next.delete(country.label);
                                else next.add(country.label);
                                return next;
                              })}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-base">{country.flag}</span>
                                <span className="text-tiny font-black uppercase tracking-widest text-[var(--text-primary)]">{country.label}</span>
                                <span className="text-3xs font-bold text-[var(--text-muted)] bg-[var(--bg-input)] px-2 py-0.5 rounded-full">{countryRoutes.length}</span>
                              </div>
                              <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${isCountryExpanded ? 'rotate-180' : ''}`} />
                            </button>
                            {isCountryExpanded && (
                              <div className="p-2 space-y-2 bg-[var(--bg-surface-alt)]">
                                {country.regions
                                  ? <>
                                      {country.regions.map(region =>
                                        renderRegionBlock(region, groupedRoutes[region] || [], true)
                                      )}
                                      {(groupedRoutes[country.key]?.length || 0) > 0 &&
                                        renderRegionBlock(country.key, groupedRoutes[country.key] || [], true)
                                      }
                                    </>
                                  : renderRegionBlock(country.key, groupedRoutes[country.key] || [], true)
                                }
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {unsortedBlock}
                    </>
                  );
                })()}
              </div>
            </div>
          )
        )}

        {sidebarMode === 'discover' && mapCenter && onDiscoverSelect && (
          <div className="h-full overflow-y-auto">
            <DiscoverPanel
              weather={discoverWeather}
              attractions={discoverAttractions}
              heritage={discoverHeritage}
              loading={discoverLoading}
              error={discoverError}
              center={mapCenter}
              selectedPoiId={discoverSelectedId}
              onSelectItem={onDiscoverSelect}
              onFilterChange={onDiscoverFilterChange}
              user={user}
              onOpenAuth={onOpenAuth}
            />
          </div>
        )}

        {sidebarMode === 'news' && (
          <div className="h-full flex flex-col overflow-hidden">
            {/* Reisefilm-promo øverst — over premium-gaten så ALLE innloggede med
                en reise å vise ser den (news er default-landingsflaten). */}
            {replayPromoEligible && onOpenReplay && onOpenStickerBoard && (
              <div className="px-4 pt-3 shrink-0">
                <ReplayPromoCard
                  eligible={replayPromoEligible}
                  uid={user?.uid}
                  onOpenReplay={onOpenReplay}
                  onOpenStickerBoard={onOpenStickerBoard}
                />
              </div>
            )}
            {!user ? (
              <AuthNudge
                title={t('auth.news_title', 'Se siste nytt')}
                description={t('auth.news_desc', 'Logg inn for å se nye steder, bilder og anmeldelser fra andre brukere i sanntid.')}
                onOpenAuth={onOpenAuth}
              />
            ) : !canUsePremiumNews ? (
              <div className="p-4">
                <div className="rounded-3xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-[var(--bg-surface)] to-[var(--bg-surface-alt)] p-5 text-center">
                  <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
                    <Sparkles size={24} className="text-amber-300" />
                  </div>
                  <p className="text-2xs font-black uppercase tracking-[0.22em] text-amber-300">
                    {t("auto.spotlist.supporter", "Supporter")}
                  </p>
                  <h3 className="mt-2 text-lg font-black text-[var(--text-primary)]">
                    {t('sidebar.news')}
                  </h3>
                  <p className="mt-2 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                    {t('premium_nudge.news_feed', 'Nyhetsfeeden med nye plasser og ferske anmeldelser er for Supportere. Oppgrader for å følge aktiviteten i sanntid.')}
                  </p>
                  <button
                    type="button"
                    onClick={onOpenUpgrade}
                    className="mt-5 w-full rounded-2xl bg-amber-500 px-4 py-3 text-tiny font-black uppercase tracking-widest text-black hover:bg-amber-400 transition active:scale-95"
                  >
                    {t('premium_nudge.btn_upgrade')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Filter-chips — én sammenslått kronologisk feed */}
                <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-[var(--border-primary)] shrink-0">
                  {([
                    { id: 'all', icon: <Newspaper size={11} />, label: t('sidebar.all', 'Alle') },
                    { id: 'spots', icon: <ShieldCheck size={11} />, label: t('sidebar.new_spots') },
                    { id: 'reviews', icon: <MessageSquare size={11} />, label: t('sidebar.reviews') },
                  ] as { id: NewsFilter; icon: React.ReactNode; label: string }[]).map(chip => (
                    <button
                      key={chip.id}
                      onClick={() => setNewsFilter(chip.id)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-2xs font-black uppercase tracking-widest transition ${newsFilter === chip.id ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] border border-transparent'}`}
                    >
                      {chip.icon} {chip.label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', willChange: 'scroll-position', transform: 'translateZ(0)' }}>
                  {/* Trial active banner for logged-in users in their first 7 days */}
                  {!isAdminUser(user) && user.subscriptionStatus !== 'paid' && isWithinTrialPeriod(user) && (
                    <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-r from-amber-500/10 to-orange-500/10 px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                          <Star size={16} className="text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-tiny font-bold text-[var(--text-primary)]">{t("auto.spotlist.proveperiode_aktiv", "Prøveperiode aktiv")}</p>
                            <span className="text-3xs font-black uppercase tracking-widest text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded">Trial</span>
                          </div>
                          <p className="text-2xs text-[var(--text-secondary)] mt-0.5">
                            Du har {Math.max(1, Math.ceil((7 * 24 * 60 * 60 * 1000 - (Date.now() - (user.creationTimestamp || 0))) / (24 * 60 * 60 * 1000)))} dager igjen med full Supporter-tilgang.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {loadingNews ? (
                    <div className="space-y-3">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="animate-pulse flex items-center gap-3 px-3 py-2.5 rounded-xl">
                          <div className="w-10 h-10 rounded-lg bg-[var(--bg-input)]" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3 bg-[var(--bg-input)] rounded w-2/3" />
                            <div className="h-2 bg-[var(--bg-input)] rounded w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : newsFeedItems.length === 0 ? (
                    <div className="text-center py-10 opacity-50">
                      {newsFilter === 'reviews'
                        ? <MessageSquare className="mx-auto text-[var(--text-muted)] mb-2" size={32} />
                        : <Newspaper className="mx-auto text-[var(--text-muted)] mb-2" size={32} />}
                      <p className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)]">
                        {newsFilter === 'reviews'
                          ? t('sidebar.no_reviews_yet')
                          : newsFilter === 'spots'
                            ? t('sidebar.no_new_spots')
                            : t('sidebar.no_news_yet', 'Ingen aktivitet ennå')}
                      </p>
                    </div>
                  ) : (
                    newsFeedItems.map(item => item.type === 'spot' ? (
                      <NewsSpotItem
                        key={`spot-${item.id}`}
                        spot={item.spot}
                        isSelected={item.id === selectedSpotId}
                        address={newsAddresses[item.id]}
                        onSelect={onSelectNewsSpot!}
                        onSelectById={onSelectSpot}
                        hasSelectHandler={!!onSelectNewsSpot}
                        t={t}
                      />
                    ) : (
                      <NewsReviewItem
                        key={`review-${item.id}`}
                        review={item.review}
                        spot={(allSpots || spots).find(s => s.id === item.review.spotId) || recentNewsSpots.find(s => s.id === item.review.spotId)}
                        onSelectSpot={onSelectSpot}
                        t={t}
                      />
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {!isDraftRouteDetail && (sidebarMode === 'spots' || sidebarMode === 'favorites') && (
          <div className="h-full flex flex-col overflow-hidden">
            {sidebarMode === 'favorites' && !user ? (
              <AuthNudge
                title={t('auth.favorites_title', 'Dine favoritter')}
                description={t('auth.favorites_desc', 'Logg inn for å lagre steder og ruter du vil besøke senere.')}
                onOpenAuth={onOpenAuth}
              />
            ) : (
              <div className="h-full flex flex-col">

            {/* ── Kompakt, 1-linjes Filter & Sorteringsbar (Progressive Disclosure) ──── */}
            {sidebarMode === 'spots' && showFilterControls && (
              <div className="shrink-0 px-3 md:px-4 py-2 border-b border-[var(--border-primary)] bg-[var(--bg-surface-alt)]">
                <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {/* Hoved Filter-knapp med teller */}
                  <button
                    type="button"
                    onClick={() => setIsFilterModalOpen(true)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-2xs font-black uppercase tracking-wide transition border shadow-sm active:scale-95 ${
                      totalActiveFilterCount > 0
                        ? 'bg-brand text-stone-900 border-brand shadow-brand/20'
                        : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] border-[var(--border-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-primary)]'
                    }`}
                  >
                    <Sliders size={12} strokeWidth={2.5} />
                    <span>{t('sidebar.filters.filter_button', 'Filter')}{totalActiveFilterCount > 0 ? ` (${totalActiveFilterCount})` : ''}</span>
                  </button>

                  {/* Hurtig-chip: Soljakt */}
                  <button
                    type="button"
                    onClick={handleToggleSunFilter}
                    disabled={gettingPos}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-2xs font-bold transition border active:scale-95 ${
                      sunFilterConfig.enabled
                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/50'
                        : 'bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    <span>☀️</span>
                    <span>{t('weather.sun', 'Soljakt')}</span>
                    {loadingForecasts && <Loader2 size={10} className="animate-spin text-amber-400 ml-0.5" />}
                  </button>

                  {/* Hurtig-chips: Egenskaper */}
                  {filterChips.map(f => {
                    const isSelected = activeFilters.has(f.id);
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => onToggleFilter?.(f.id)}
                        title={f.tooltip ?? f.label}
                        aria-label={f.tooltip ? `${f.label}. ${f.tooltip}` : f.label}
                        className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-2xs font-bold transition border active:scale-95 ${
                          isSelected
                            ? 'bg-sky-500/20 text-sky-300 border-sky-500/50'
                            : 'bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:text-[var(--text-secondary)]'
                        }`}
                      >
                        <span>{f.emoji}</span>
                        <span>{f.label}</span>
                        {isSelected && (filterChipCounts[f.id] ?? 0) > 0 && (
                          <span className="ml-0.5 text-3xs opacity-80">({filterChipCounts[f.id]})</span>
                        )}
                      </button>
                    );
                  })}

                  {/* Sortering hurtigvalg: Nærmest */}
                  <button
                    type="button"
                    onClick={handleToggleSortByDistance}
                    disabled={gettingPos}
                    className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-2xs font-bold transition border active:scale-95 ${
                      sortByDistance && !sunFilterConfig.enabled
                        ? 'bg-brand/20 text-emerald-400 border-brand/40'
                        : 'bg-[var(--bg-surface)] text-[var(--text-muted)] border-[var(--border-secondary)] hover:text-[var(--text-secondary)]'
                    }`}
                  >
                    <Navigation size={10} />
                    <span>{sortByDistance ? `${t('sidebar.distance')} ↑` : t('sidebar.distance')}</span>
                  </button>

                  {/* Nullstill-knapp ved aktive filtre */}
                  {totalActiveFilterCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        onClearFilters?.();
                        if (sunFilterConfig.enabled) handleToggleSunFilter();
                        setSortByDistance(false);
                        setSortByPopularity(false);
                      }}
                      className="shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-2xs font-bold text-red-400 border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition"
                    >
                      <X size={10} />
                      <span>{t('common.reset', 'Nullstill')}</span>
                    </button>
                  )}
                </div>

                {/* Subtil soljakt-indikator hvis aktiv */}
                {sunFilterConfig.enabled && (
                  <div className="flex items-center justify-between gap-2 px-2.5 py-1 mt-1.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-3xs text-amber-300">
                    <span className="truncate">
                      ☀️ {sunFilterConfig.period === 'today' ? 'Sol i dag' : sunFilterConfig.period === 'tomorrow' ? 'Sol i morgen' : sunFilterConfig.period === 'weekend' ? 'Sol i helgen' : 'Sol/opphold 3 dager'}
                      {nearestSunSpotInfo ? ` · ${filteredSpots.length} funnet (nærmeste: ${nearestSunSpotInfo.distLabel})` : ''}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsFilterModalOpen(true)}
                      className="text-amber-400 underline uppercase tracking-wider font-extrabold shrink-0"
                    >
                      {t('common.adjust', 'Endre')}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Scrollbart innhold ─────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar" style={{ overscrollBehavior: 'contain' }}>

              {/* Mobil: sort + filter (ruller bort) */}
              {sidebarMode === 'spots' && (
                <div className="md:hidden space-y-2">
                  {canReturnToCollabMap && onReturnToCollabMap && (
                    <button
                      onClick={onReturnToCollabMap}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-2xs font-black uppercase tracking-wide border border-sky-500/35 bg-sky-500/10 text-sky-300 hover:bg-sky-500/15 transition"
                    >
                      <ArrowLeft size={11} />
                      {t("auto.spotlist.tilbake_til_mappe", "Tilbake til mappe")}
                    </button>
                  )}
                  {canReturnToRoute && onReturnToRoute && (
                    <button
                      onClick={onReturnToRoute}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-2xs font-black uppercase tracking-wide border border-brand/35 bg-brand/10 text-emerald-300 hover:bg-brand/15 transition"
                    >
                      <ArrowLeft size={11} />
                      {t("auto.spotlist.tilbake_til_rute", "Tilbake til rute")}
                    </button>
                  )}

                  {nearbyFavorite && (
                    <div className="flex items-center gap-2.5 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2 animate-in slide-in-from-top-2 fade-in duration-300">
                      <Bell size={14} className="text-amber-400 shrink-0 animate-pulse" />
                      <div className="flex-1 min-w-0">
                        <p className="text-2xs font-black text-amber-300 uppercase tracking-wide">{favoriteSpotIds.includes(nearbyFavorite!.id) ? t('sidebar.nearby_favorite') : t('sidebar.nearby_spot')}</p>
                        <p className="text-2xs text-amber-400/70 truncate">{nearbyFavorite.name}</p>
                      </div>
                      <button onClick={() => onSelectSpot(nearbyFavorite.id)}
                        className="text-3xs font-black text-amber-300 bg-amber-500/20 px-2 py-1 rounded-lg hover:bg-amber-500/30 transition-colors shrink-0">{t('common.open')}</button>
                      <button onClick={() => setProximityDismissed(nearbyFavorite.id)}
                        className="text-amber-600 hover:text-amber-300 transition-colors shrink-0"><X size={13} /></button>
                    </div>
                  )}
                </div>
              )}

              {/* Desktop: proximity alert øverst i scrollbart felt */}
              {sidebarMode === 'spots' && nearbyFavorite && (
                <div className="hidden md:flex items-center gap-2.5 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2 animate-in slide-in-from-top-2 fade-in duration-300">
                  <Bell size={14} className="text-amber-400 shrink-0 animate-pulse" />
                  <div className="flex-1 min-w-0">
                    <p className="text-2xs font-black text-amber-300 uppercase tracking-wide">{favoriteSpotIds.includes(nearbyFavorite!.id) ? t('sidebar.nearby_favorite') : t('sidebar.nearby_spot')}</p>
                    <p className="text-2xs text-amber-400/70 truncate">{nearbyFavorite.name}</p>
                  </div>
                  <button onClick={() => onSelectSpot(nearbyFavorite.id)}
                    className="text-3xs font-black text-amber-300 bg-amber-500/20 px-2 py-1 rounded-lg hover:bg-amber-500/30 transition-colors shrink-0">{t('common.open')}</button>
                  <button onClick={() => setProximityDismissed(nearbyFavorite.id)}
                    className="text-amber-600 hover:text-amber-300 transition-colors shrink-0"><X size={13} /></button>
                </div>
              )}

              {sidebarMode === 'spots' && canReturnToCollabMap && onReturnToCollabMap && (
                <button
                  onClick={onReturnToCollabMap}
                  className="hidden md:flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-2xs font-black uppercase tracking-wide border border-sky-500/35 bg-sky-500/10 text-sky-300 hover:bg-sky-500/15 transition"
                >
                  <ArrowLeft size={11} />
                  {t("auto.spotlist.tilbake_til_mappe", "Tilbake til mappe")}
                </button>
              )}
              {sidebarMode === 'spots' && canReturnToRoute && onReturnToRoute && (
                <button
                  onClick={onReturnToRoute}
                  className="hidden md:flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-2xs font-black uppercase tracking-wide border border-brand/35 bg-brand/10 text-emerald-300 hover:bg-brand/15 transition"
                >
                  <ArrowLeft size={11} />
                  {t("auto.spotlist.tilbake_til_rute", "Tilbake til rute")}
                </button>
              )}


              {isLoading && !isAnalyzing ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[var(--bg-input)]" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-[var(--bg-input)] rounded w-3/4" />
                          <div className="h-2 bg-[var(--bg-input)] rounded w-1/2" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-[var(--bg-input)] rounded-full w-16" />
                        <div className="h-6 bg-[var(--bg-input)] rounded-full w-20" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredSpots.length === 0 ? (
                <div className="text-center py-6 px-2 space-y-4">
                  <div className="space-y-1">
                    <Tent className="mx-auto text-[var(--text-muted)] mb-1" size={30} />
                    <p className="text-tiny font-black uppercase tracking-widest text-[var(--text-muted)]">
                      {sidebarMode === 'favorites' ? t('sidebar.no_favorites') : t('sidebar.no_spots_in_view')}
                    </p>
                    <p className="text-2xs text-[var(--text-muted)]">{t('sidebar.zoom_or_pan', 'Zoom ut eller flytt kartet')}</p>
                  </div>

                  {/* Nærmeste plass med sol når solfilter er aktivt */}
                  {sidebarMode === 'spots' && sunFilterConfig.enabled && nearestSunSpotInfo && (
                    <div className="mx-auto max-w-sm p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-left space-y-2 animate-in fade-in duration-300 shadow-sm">
                      <div className="flex items-center justify-between text-2xs font-black uppercase tracking-wider text-amber-400">
                        <span className="flex items-center gap-1.5">
                          <Sun size={12} className="fill-amber-400 text-amber-400" /> Nærmeste plass med sol
                        </span>
                        <span className="text-amber-300 font-extrabold text-xs">{nearestSunSpotInfo.distLabel}</span>
                      </div>
                      <p className="text-xs text-[var(--text-primary)] font-bold truncate">
                        {nearestSunSpotInfo.spot.name}
                      </p>
                      <p className="text-3xs text-[var(--text-muted)]">
                        Beregnet reisetid: <span className="text-amber-300 font-semibold">{nearestSunSpotInfo.etaLabel}</span>
                      </p>
                      <div className="pt-1 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleShowNearestSpot(nearestSunSpotInfo.spot)}
                          className="px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-stone-950 text-2xs font-black uppercase tracking-wide transition shadow-sm flex items-center gap-1.5 font-bold"
                        >
                          <Navigation size={10} /> Vis på kartet
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Nærmeste fricamp uavhengig av filter / utsnitt (kun når IKKE soljakt) */}
                  {sidebarMode === 'spots' && !sunFilterConfig.enabled && nearestAnySpotInfo && (
                    <div className="mx-auto max-w-sm p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-left space-y-2 animate-in fade-in duration-300">
                      <div className="flex items-center justify-between text-2xs font-black uppercase tracking-wider text-emerald-400">
                        <span className="flex items-center gap-1.5">
                          <Navigation size={12} className="animate-pulse" /> Nærmeste fricamp
                        </span>
                        <span className="text-emerald-300 font-extrabold text-xs">{nearestAnySpotInfo.distLabel}</span>
                      </div>
                      <p className="text-xs text-[var(--text-primary)] font-bold truncate">
                        {nearestAnySpotInfo.spot.name}
                      </p>
                      <p className="text-3xs text-[var(--text-muted)]">
                        Beregnet reisetid: <span className="text-emerald-300 font-semibold">{nearestAnySpotInfo.etaLabel}</span>
                      </p>
                      <div className="pt-1 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleShowNearestSpot(nearestAnySpotInfo.spot)}
                          className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-stone-950 text-2xs font-black uppercase tracking-wide transition shadow-sm flex items-center gap-1.5"
                        >
                          <Navigation size={10} /> Vis på kartet
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Nærmeste med aktive filtre hvis filter ga 0 treff */}
                  {sidebarMode === 'spots' && !sunFilterConfig.enabled && nearestFilteredSpotInfo && activeFilters && activeFilters.size > 0 && (
                    <div className="mx-auto max-w-sm p-3 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] text-left text-2xs text-[var(--text-muted)] space-y-1.5">
                      <p>
                        Nærmeste med dine valgte filtre er <strong className="text-emerald-300">{nearestFilteredSpotInfo.distLabel}</strong> unna ({nearestFilteredSpotInfo.spot.name}).
                      </p>
                      <button
                        type="button"
                        onClick={() => handleShowNearestSpot(nearestFilteredSpotInfo.spot)}
                        className="text-3xs font-black uppercase text-emerald-400 hover:underline flex items-center gap-1"
                      >
                        <Navigation size={9} /> Gå til denne
                      </button>
                    </div>
                  )}

                  {sidebarMode === 'favorites' ? (
                    <button
                      onClick={() => setSidebarMode('spots')}
                      className="text-2xs font-bold text-emerald-400 hover:text-emerald-300 transition-colors underline underline-offset-2"
                    >
                      {t('sidebar.explore_spots', 'Utforsk plasser')}
                    </button>
                  ) : sunFilterConfig.enabled ? (
                    <div className="space-y-2 pt-1">
                      <p className="text-2xs text-amber-300/80">
                        {loadingForecasts
                          ? 'Henter værvarsel for plasser i nærområdet...'
                          : `Ingen plasser med sol funnet innen ${sunFilterConfig.maxDistanceKm > 0 ? `${sunFilterConfig.maxDistanceKm} km` : 'rekkevidde'}.`}
                      </p>
                      {nearestAnySpotInfo && !loadingForecasts && !nearestSunSpotInfo && (
                        <p className="text-3xs text-[var(--text-muted)]">
                          (Nærmeste fricamp uavhengig av vær er {nearestAnySpotInfo.spot.name} – {nearestAnySpotInfo.distLabel} unna)
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => setShowSunSettingsModal(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-2xs font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition"
                      >
                        <Sliders size={12} /> Juster søkeavstand eller periode
                      </button>
                    </div>
                  ) : (
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={handleToggleSunFilter}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-2xs font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition"
                      >
                        <Sun size={12} className="text-amber-400" /> ☀️ Finn nærmeste med sol
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                filteredSpots.map(spot => {
                  const visitedAt = visits.find(v => v.spotId === spot.id)?.visitedAt;
                  return (
                    <SpotCard
                      key={spot.id}
                      spot={spot}
                      isSelected={spot.id === selectedSpotId}
                      compact={sidebarMode === 'favorites'}
                      activeHikingTrailId={activeHikingTrailId}
                      isFavorite={favoriteSpotIds.includes(spot.id)}
                      isVisited={visitedSpotIds.includes(spot.id)}
                      visitedAt={visitedAt}
                      nearbySpots={spot.id === selectedSpotId ? nearbyForSelected : undefined}
                      collaborativeMaps={collaborativeMaps}
                      onSelect={(s) => onSelectSpot(s.id)}
                      onSelectHikingTrail={onSelectHikingTrail}
                      onRemoveSpot={onRemoveSpot}
                      onToggleFavorite={(e, id) => onToggleFavoriteSpot?.(e, id)}
                      onToggleVisit={(e, id) => onToggleVisit?.(e, id)}
                      onAddPastStay={onAddPastStay}
                    onDeletePastStay={onDeletePastStay}
                      onMarkOccupied={onMarkOccupied}
                      onExtendOccupancy={onExtendOccupancy}
                      onClearOccupancy={onClearOccupancy}
                      userPosition={userPosition}
                      onAddToMap={(e, id) => onAddToMap?.(e, id)}
                      onReanalyze={onReanalyze}
                      onEdit={onEditSpot}
                      user={user}
                      onOpenAuth={onOpenAuth}
                      isAnalyzing={activeAnalysisIds.has(spot.id)}
                      isGeneratingMeta={generatingMetaIds.has(spot.id)}
                      onSaveSearchResult={onSaveSearchResult}
                      onFitBounds={onFitBounds}
                      onCreateRouteFromSpot={onCreateRouteFromSpot}
                      isCreatingRoute={creatingRouteSpotId === spot.id}
                      onShowNearestBarrier={onShowNearestBarrier}
                      onEnsureSpotPersisted={onEnsureSpotPersisted}
                      onNavigateInApp={onNavigateInApp}
                    />
                  );
                })
              )}
            </div>
          </div>
            )}
          </div>
        )}
      </div>
      {/* Soljakt Innstillinger-modal */}
      <SunFilterModal
        isOpen={showSunSettingsModal}
        onClose={() => setShowSunSettingsModal(false)}
        config={sunFilterConfig}
        onApply={(newConfig) => {
          setSunFilterConfig(newConfig);
          try { localStorage.setItem('fricamp_sun_filter_config', JSON.stringify(newConfig)); } catch {}
          if (!effectiveUserPos) {
            handleToggleSunFilter();
          } else {
            setSortByDistance(true);
          }
        }}
        candidateCount={sunFilterConfig.enabled ? filteredSpots.length : null}
      />
      {/* Nytt felles Filter & Sortering bunnark/modal */}
      <SpotFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        sortByDistance={sortByDistance}
        onToggleSortByDistance={handleToggleSortByDistance}
        sortByPopularity={sortByPopularity}
        onToggleSortByPopularity={() => {
          setSortByPopularity(v => !v);
          if (!sortByPopularity) setSortByDistance(false);
        }}
        sunFilterConfig={sunFilterConfig}
        onUpdateSunFilterConfig={(cfg) => {
          setSunFilterConfig(cfg);
          try { localStorage.setItem('fricamp_sun_filter_config', JSON.stringify(cfg)); } catch {}
        }}
        activeFilters={activeFilters}
        onToggleFilter={(fId) => onToggleFilter?.(fId)}
        onClearFilters={() => {
          onClearFilters?.();
          if (sunFilterConfig.enabled) handleToggleSunFilter();
          setSortByDistance(false);
          setSortByPopularity(false);
        }}
        isLoggedIn={!!user}
        matchCount={filteredSpots.length}
      />
    </div>
  );
};

export default React.memo(SpotList);
